import { create } from 'zustand'
import { authGet, authSet, authRemove } from '../utils/authStorage'
import { writeIdleStamp, clearIdleStamp } from '../utils/idleStamp'
import { clearProfileRunPending } from '../utils/profileRunPending'
import { persist } from 'zustand/middleware'
import { authAPI, casesAPI, clearToken, clearSetupToken, serverLogout, getToken, purgeLegacyAuth } from '../services/api'
import { isTokenExpired } from '../utils/authToken'
import useQuestionnaireStore from './useQuestionnaireStore'
import { useOnboardingStore } from './useOnboardingStore'
import useAgentStore from './useAgentStore'
import useNotificationStore from './useNotificationStore'
import { PROFILE_SECTION_KEYS, parseSection, hasSectionContent } from '../utils/profileSections'
import { stripQuestionText, readQuestionText, mergeQuestionText, withQuestionText } from '../utils/questionText'
import { insuranceFromDiscoveryCandidate, payerKeysForWire } from '../utils/payerPick'
import { buildDiscoveredSecondary, secondaryFromResponse, isSecondPlanOffer, discoverySnoozeKey, sameSecondaryCoverage } from '../utils/insuranceDiscovery'
import { slimProfileSection } from '../utils/slimCard'
import { pollRefused } from '../utils/pollRefused'
import { priorAuthPayerEntries } from '../utils/priorAuthStatus'
import { POST_LOGIN_KEY } from '../utils/postLoginTarget'
import { readAvsText } from '../utils/avsText' // PROD-134 gamma: the one AVS reader
import { visitHeaderOf, signedDiagnosisOf } from '../utils/signedVisit' // PROD-957: the header is the latest SIGNED visit's; PROD-1079: signed diagnosis only
import { caseHasDecline, mergeFollowUp } from '../utils/declinedRequest' // PROD-983
import { mergeDeliverFileList } from '../utils/deliverIdentity' // PROD-1103: local file list = union by identity
import { buildDeliverPatch } from '../utils/deliverPatch' // PROD-1104: the API gets only what the call changes
import { newerSummary } from '../utils/summaryFreshness'
import { clinicianName, withCaseClinician } from '../utils/clinicianName.js'

// v207.2.0: Canonicalize specialty + focus area IDs against the local catalog when loading
// cases from the API. The API stores human-readable names (e.g., "Globus (Lump in Throat)")
// but the catalog uses short canonical IDs (e.g., "globus-lump"). Algorithmic derivation
// from the name doesn't always produce the catalog ID — token-based lookup against the
// catalog is the source of truth. Returns { specialtyId, focusAreaId } as the canonical IDs.
// Falls back to the algorithmic derivation if no catalog match (preserves existing behavior
// for any focus area not yet in the catalog).
const _tokenize = (s) => (s || '').toLowerCase().replace(/[()]/g, ' ').replace(/[^a-z0-9]+/g, ' ').trim().split(/\s+/).filter(Boolean)

const canonicalizeFocusAreaId = (specialtyName, focusAreaName, derivedSpecialtyId, derivedFocusAreaId) => {
  try {
    // Use the questionnaire store's tolerant getters, which already match by id, normalized
    // id, name, and (post-v207.1) token-overlap. This guarantees we get the same answer
    // every reader of the case will get when they look up the focus area.
    const qStore = useQuestionnaireStore.getState()
    // First try with the derived IDs (covers the common case where derivation worked)
    let fa = qStore.getFocusArea(derivedSpecialtyId, derivedFocusAreaId)
    // If that fails, try matching by name directly — getFocusArea has a name-match path
    if (!fa && focusAreaName) fa = qStore.getFocusArea(derivedSpecialtyId, focusAreaName)
    // Also resolve specialty so we return its canonical id even when derivation worked
    let sp = qStore.getSpecialty(derivedSpecialtyId)
    if (!sp && specialtyName) sp = qStore.getSpecialty(specialtyName)
    return {
      specialtyId: sp?.id || derivedSpecialtyId,
      focusAreaId: fa?.id || derivedFocusAreaId,
    }
  } catch (_e) {
    // Defensive: never break loadCases on a catalog lookup failure
    return { specialtyId: derivedSpecialtyId, focusAreaId: derivedFocusAreaId }
  }
}

// Language lookup for store strings (can't use React hooks)
const _getLang = () => {
  try { return localStorage.getItem('orella-language') || 'en' } catch { return 'en' }
}

const AUTH_ERRORS = {
  en: {
    registrationFailed: 'Registration failed',
    verificationFailed: 'Verification failed',
    failedToResend: 'Failed to resend',
    failedToSetPin: 'Failed to set PIN',
    loginFailed: 'Login failed',
    failedToSendReset: 'Failed to send reset code',
    invalidVerification: 'Invalid verification code',
    failedToResetPassword: 'Failed to reset password',
    failedToResendCode: 'Failed to resend code',
    unknownError: 'Unknown error',
    requestTimedOut: 'Request timed out. Please try again.',
    networkError: 'Network error. Please check your connection or try with smaller files.',
  },
  es: {
    registrationFailed: 'Error en el registro',
    verificationFailed: 'Error en la verificación',
    failedToResend: 'Error al reenviar',
    failedToSetPin: 'Error al establecer el PIN',
    loginFailed: 'Error al iniciar sesión',
    failedToSendReset: 'Error al enviar código de recuperación',
    invalidVerification: 'Código de verificación inválido',
    failedToResetPassword: 'Error al restablecer contraseña',
    failedToResendCode: 'Error al reenviar código',
    unknownError: 'Error desconocido',
    requestTimedOut: 'Tiempo de espera agotado. Intente de nuevo.',
    networkError: 'Error de red. Revise su conexión o intente con archivos más pequeños.',
  },
}

const _authErr = (key) => {
  const lang = _getLang()
  return (AUTH_ERRORS[lang] || AUTH_ERRORS.en)[key] || AUTH_ERRORS.en[key]
}

// v242.39.0 — PHI isolation on account switch. When the signed-in account changes
// (a different user id) or a session is established fresh after logout, wipe the
// previous account's in-memory cases + profile and their persisted copies, so
// account A's data can't flash on account B in the same app session (an SPA switch
// does not reload / re-rehydrate the stores). Guarded by user id, so same-account
// setUser updates (e.g. toggling a setting) never wipe. References the case/profile
// stores at call time (runtime) — safe even though they're declared below.
function _resetPhiForUser(prevUser, nextUser) {
  const prevId = prevUser?.id != null ? String(prevUser.id) : null
  const nextId = nextUser?.id != null ? String(nextUser.id) : null
  if (!nextId || prevId === nextId) return
  try { useCaseStore.getState().resetForUser() } catch (_) {}
  try { useProfileStore.getState().resetForUser() } catch (_) {}
  try { useAgentStore.getState().resetForUser() } catch (_) {}
  try { useNotificationStore.getState().resetForUser() } catch (_) {}
  // ★★★ v242.169.0 — PHI-ISO-3. The FIFTH store, and the reason it was missed:
  //   useOnboardingStore is unpersisted and OnboardingFlow resets it on unmount,
  //   so on every path anyone walks by hand it is already empty by the time you
  //   look. The path that is not walked by hand is an in-session account switch
  //   with the flow still MOUNTED — no unmount runs, nothing rehydrates, and
  //   account A's identity, insurance, allergies and medications sit in memory
  //   under account B. Measured before adding: six stores exist; five hold
  //   per-account state; useQuestionnaireStore holds the QUESTION CATALOG and is
  //   correctly absent. `scripts/check-phi-reset.mjs` now asserts that census
  //   instead of leaving it to whoever next adds a store.
  try { useOnboardingStore.getState().reset() } catch (_) {}
  // The pre-2FA basics buffer (OnboardingFlow's DRAFT_KEY) is the same PHI in a
  // second place — sessionStorage rather than memory — and no unmount clears it.
  try { sessionStorage.removeItem('orella_onboarding_draft') } catch (_) {}
}

// ★★★ v242.157.0 — USERSTORE-TRANSIENT-PERSISTED-1. THIS STORE USED TO PERSIST
//   ITS WHOLE STATE. `persist` with no `partialize` writes every key on every
//   set(), so `error` and `loading` — which describe ONE request — were written
//   to storage and rehydrated on a cold start. Two things fell out of it:
//
//   (1) "This account uses Apple sign-in" survived closing the app. SignInScreen
//       renders `localError || error` (:407); `localError` dies with the mount
//       and `error` did not, so the banner came back on a screen with empty
//       fields — and rendered in the RED fallback branch (:411) instead of the
//       blue `sso_only` one, because `errorType` is component state and HAD
//       reset. The wrong colour is the tell that the string came from storage.
//   (2) `loading:true` is written BEFORE the network call in login(). A kill
//       mid-request — which iOS does to the WKWebView content process on memory
//       pressure, see utils/authStorage.js — persisted it, and SignInScreen
//       disables the submit button on `loading` (:513). The button came up dead
//       and stayed dead. No `finally` can fix that one; not persisting it does.
//
//   ★★ THE HOLE WAS ALREADY KNOWN AND PATCHED FOR ONE KEY: see `pendingUserId`
//      below, v242.87.0, whose comment names this exact mechanism. Patching one
//      key left the mechanism in place. THE LISTS BELOW ARE THE MECHANISM'S FIX,
//      and `scripts/check-store-persistence.mjs` is what makes them a property:
//      every key declared in this store must appear in EXACTLY ONE of them, so
//      a new key cannot be added without someone deciding which it is.
export const USER_STORE_TRANSIENT_KEYS = ['loading', 'error']

export const USER_STORE_PERSISTED_KEYS = [
  'user', 'isAuthenticated',
  'pendingUserId', 'pendingToken', 'pendingPhone', 'pendingEmail', 'pendingProfile',
  'isLocked', 'lockedAt', 'lockedRoute', 'pinFailedAttempts',
  'awayAt', 'authAt',
]

// ★★★ v242.160.0 — USERSTORE-TRANSIENT-PERSISTED-1 WAS FIXED IN ONE STORE OF THREE,
//     AND THE OTHER TWO SHIPPED THE SAME DEADLOCK TO A DEVICE.
//
//     D392 named the mechanism — a `persist()` with no `partialize` writes
//     in-flight flags to disk, and a process kill runs no JS, so no `finally`
//     can clear them. It then patched `useUserStore` and left `useProfileStore`
//     and `useCaseStore` persisting theirs. Build 52 went to TestFlight with a
//     WORSE instance of the same defect than the one it was cut to fix:
//
//       App.jsx:166   if (!profile?.profileLoaded && !loading) loadProfile()
//
//     `loading` is the profile store's, and it is the ONLY caller that sets
//     `onboardingLoaded`, which is the ONLY condition releasing the
//     protected-route hold at App.jsx:197. A `loading:true` that survives a kill
//     therefore blocks EVERY protected route behind a full-screen loader,
//     permanently, with reinstall the only escape. Reported from Build 52.
//
// ★★ THE LESSON IS ABOUT THE GATE, NOT THE STORE. `check-store-persistence`
//    was aimed at `useUserStore` by name — it encoded the instance it was born
//    from (§1bd(a)) — so it was green over both remaining copies. It now walks
//    EVERY `persist(` call in this file and requires each to declare its lists.
// discoveryToastSnoozedFor (PROD-306): a session-only snooze of Home's discovery toast.
export const PROFILE_STORE_TRANSIENT_KEYS = ['loading', 'syncing', 'discoveryToastSnoozedFor']

export const PROFILE_STORE_PERSISTED_KEYS = ['profile']

// ★★★ PROD-616 (v242.238.3) — THE SIGN-UP PASSWORD WAS PERSISTED IN `profile`. Both branches of
//   SignUpScreen.handleSubmit called updateProfile(form) with the whole form, updateProfile spread it
//   unfiltered, and `profile` is persisted whole, so the password sat at $.state.profile.password
//   (sessionStorage on web, localStorage on native, where it survived an app restart) until 2FA
//   setup, sign-in or logout. No key below is a profile field; the store drops them in three places:
//   its own set() (memory: updateProfile and every other action, see scrubProfileWrites), partialize
//   (what is written) and merge (what is read back). The sign-up screen passes only the profile
//   basics (firstName, lastName, email, phone) since the same release. persist
//   `version: 1` with `migrate` makes zustand rewrite a version-0 blob on first boot, so a device that
//   ran v242.238.2 loses the password from storage, not only from memory.
//   Held by scripts/check-credential-persistence.mjs.
export const PROFILE_CREDENTIAL_KEYS = [
  'password', 'confirmPassword', 'currentPassword', 'newPassword',
  'pin', 'confirmPin', 'newPin',
  'token', 'accessToken', 'refreshToken', 'setupToken', 'pendingToken', 'resetAuthToken', 'turnstileToken',
]

export function withoutCredentialKeys(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj
  const clean = { ...obj }
  for (const k of PROFILE_CREDENTIAL_KEYS) delete clean[k]
  return clean
}

// The persisted shape { profile, … } with `profile` cleaned; every other key as it is.
export function withoutProfileCredentials(state) {
  if (!state || typeof state !== 'object' || !state.profile || typeof state.profile !== 'object') return state
  return { ...state, profile: withoutCredentialKeys(state.profile) }
}

// ★★ Layer (b) in memory: useProfileStore's own set() drops credential keys from every `profile` it
//   writes, so updateProfile, loadProfile and every other action of the store write a clean profile
//   whatever they are handed. (It wraps set rather than editing updateProfile, so the gates that
//   lift updateProfile's text and run it keep running the same text.)
function scrubProfileWrites(config) {
  return (set, get, api) => config(
    (partial, replace) => set(
      typeof partial === 'function' ? (s) => withoutProfileCredentials(partial(s)) : withoutProfileCredentials(partial),
      replace,
    ),
    get,
    api,
  )
}

export const CASE_STORE_TRANSIENT_KEYS = ['syncing', 'casesLoading', 'casesLoadFailed']

export const CASE_STORE_PERSISTED_KEYS = ['cases', 'currentCase', 'deletedCaseIds']

export const useUserStore = create(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      loading: false,
      error: null,
      
      // Pending registration data (before verification complete)
      pendingUserId: null,
      pendingToken: null, // v242.6.0 (C/#11): signed pending-signup token (deferred creation)
      pendingPhone: null,
      pendingEmail: null, // v242.6.0: masked email for the verify screen
      pendingProfile: null, // v228.1.1: signup basics, seeded into onboarding pre-auth

      // v237.6.0: Session PIN lock state (lock-on-return). Mirrors MD panel.
      isLocked: false,
      lockedAt: null,
      lockedRoute: null,
      pinFailedAttempts: 0,
      // v242.130.0: away/auth stamps — see stampAway below.
      awayAt: null,
      authAt: null,

      // v242.87.0: clear `pendingUserId` on sign-in. It is the id of a user
      // mid-verification; once a real user is set it is stale, and because this
      // store persisted with no partialize it survived into later sessions where
      // TwoFactorSetup would happily set up 2FA against the WRONG account.
      // ★ v242.157.0: the store now HAS a partialize, and `pendingUserId` is
      //   deliberately still on the persisted list — a patient mid-verification
      //   may well close the app before the SMS arrives. Clearing it here is
      //   still what makes it safe; see USER_STORE_PERSISTED_KEYS above.
      // v242.130.0: `authAt` is the REAL sign-in moment. The privacy hook's
      // post-login grace period used to key off the hook's own mount, which the
      // per-navigation remount turned into "3 seconds after every screen change"
      // — a blind window in which leaving the app was never recorded at all.
      // PROD-252 (P17 step 10): a sign-in starts a fresh foreground idle clock.
      // PROD-1203: a NEW sign-in clears an earlier PIN lock; a profile update of the same
      //   live session (same id, already authenticated) never does. A caller that is a full
      //   sign-in with credentials/SSO says so with { freshSignIn: true } (same id as the lock).
      //   Only a real sign-in or the PIN unlock lifts a lock: not a reload, refresh or profile load.
      setUser: (user, opts) => {
        const prev = get()
        const fresh = !!user && ((opts && opts.freshSignIn === true) || !prev.isAuthenticated || prev.user?.id !== user.id)
        _resetPhiForUser(prev.user, user)
        const now = Date.now()
        if (fresh) clearProfileRunPending() // PROD-1192 (L2a): a fresh sign-in drops any earlier user's marker
        set({ user, isAuthenticated: !!user, pendingUserId: null, authAt: now, awayAt: null, ...(fresh ? { isLocked: false, lockedAt: null, lockedRoute: null, pinFailedAttempts: 0 } : {}) })
        if (user) writeIdleStamp({ lastActivityAt: now, hiddenAt: null })
      },

      // v237.6.0: Lock the session (called by privacy hook on return when PIN enabled)
      // v242.14.0: `awayAt` anchors the PIN window to when the user navigated
      // away, not to when the lock fires on return, so the resume window counts
      // down from leave time. Falls back to now (e.g. the idle-timeout lock,
      // where the user was present right up until it fired).
      lockSession: (currentRoute, awayAt) =>
        set({ isLocked: true, lockedAt: awayAt || Date.now(), lockedRoute: currentRoute || null, pinFailedAttempts: 0 }),

      // Unlock after a successful PIN verify
      // PROD-252 (P17 step 10): a PIN unlock starts a fresh foreground idle clock.
      unlockSession: () => {
        set({ isLocked: false, lockedAt: null, pinFailedAttempts: 0, awayAt: null })
        writeIdleStamp({ lastActivityAt: Date.now(), hiddenAt: null })
      },

      // ★★★ v242.130.0 — THE AWAY STAMP LIVES HERE, NOT IN A COMPONENT REF.
      //     It used to be `hiddenAtRef` inside `usePrivacyScreen`, which is
      //     mounted only by `MobileLayout` — and `App.jsx` keys `<Routes>` on
      //     `location.pathname`, so the router DESTROYS AND REBUILDS that
      //     component on every navigation. A ref cannot outlive the thing that
      //     holds it, so the away clock was routinely absent at the moment the
      //     patient actually came back, and an absent clock read as "carry on".
      //     In the store it survives navigation, remount, and a cold start.
      //     Idempotent by design: the FIRST away signal wins, so a second one
      //     (visibilitychange and appStateChange both firing) cannot reset it.
      stampAway: (ts) =>
        set((state) => (state.awayAt == null ? { awayAt: ts || Date.now() } : {})),

      clearAway: () => set({ awayAt: null }),

      // PROD-1192 (Alex 2026-10-19): the moment setup completes, nothing from setup may lock
      // the first Home render — no away stamp taken while setup ran, and a fresh foreground
      // idle clock (the setup screens were foreground activity the idle clock never saw).
      clearAwayForSetupEnd: () => {
        set({ awayAt: null })
        writeIdleStamp({ lastActivityAt: Date.now(), hiddenAt: null })
      },

      // Track a failed PIN attempt; 3 failures → full logout
      incrementPinFailures: () => {
        const attempts = get().pinFailedAttempts + 1
        if (attempts >= 3) {
          get().logout()
          return { forcedLogout: true }
        }
        set({ pinFailedAttempts: attempts })
        return { forcedLogout: false, attemptsRemaining: 3 - attempts }
      },

      // Whether the PIN window (default 30 min) has elapsed since lock
      isPinWindowExpired: () => {
        const { lockedAt, user } = get()
        if (!lockedAt) return false
        const windowMinutes = user?.sessionPinWindow || 30
        return (Date.now() - lockedAt) / (1000 * 60) > windowMinutes
      },
      
      register: async (email, password, firstName, lastName, phone, smsOptIn = false, turnstileToken = null) => {
        set({ loading: true, error: null })
        try {
          const response = await authAPI.register(email, password, firstName, lastName, phone, smsOptIn, turnstileToken)
          
          // Don't set authenticated yet - need to verify. v242.6.0 (C/#11): the
          // account is NOT created server-side yet — we hold a signed pending
          // token and verify phone then email before the row is written.
          set({ 
            loading: false,
            pendingToken: response.pendingToken || null,
            pendingUserId: response.userId || null, // legacy API fallback
            pendingPhone: response.phoneMasked || phone,
            pendingEmail: response.emailMasked || email,
            // v228.1.1: hold signup basics so onboarding (which now runs
            // pre-auth, before `user` is set) can pre-fill the Basics screen.
            // s95: the email sign-up form requires the Terms checkbox, so the Agreements
            // step records that acceptance instead of asking again (SSO sign-ups are asked there).
            pendingProfile: { firstName, lastName, email, phone, termsAccepted: true },
          })
          
          return { 
            success: true, 
            smsSent: response.smsSent,
            phoneMasked: response.phoneMasked,
            emailMasked: response.emailMasked
          }
        } catch (err) {
          set({ loading: false, error: err.error || _authErr('registrationFailed') })
          return { success: false, error: err.error, code: err.code }
        }
      },
      
      // v242.6.0 (C/#11): step 1 — verify the phone code. On success the server
      // sends the email code and returns an advanced token; nothing is created yet.
      verifyPhone: async (code) => {
        const token = get().pendingToken
        if (!token) {
          return { success: false, error: 'No pending registration' }
        }
        set({ loading: true, error: null })
        try {
          const response = await authAPI.verify({ pendingToken: token, channel: 'phone', code })
          set({
            loading: false,
            pendingToken: response.pendingToken || token,
            pendingEmail: response.emailMasked || get().pendingEmail,
          })
          return { success: true, emailSent: response.emailSent }
        } catch (err) {
          set({ loading: false, error: err.error || _authErr('verificationFailed') })
          return { success: false, error: err.error }
        }
      },

      // v242.6.0 (C/#11): step 2 — verify the email code. On success the account
      // row is created and its userId returned (used by onboarding).
      verifyEmail: async (code) => {
        const token = get().pendingToken
        if (!token) {
          return { success: false, error: 'No pending registration' }
        }
        set({ loading: true, error: null })
        try {
          const response = await authAPI.verify({ pendingToken: token, channel: 'email', code })
          set({ loading: false, pendingUserId: response.userId, pendingToken: null })
          return { success: true, userId: response.userId }
        } catch (err) {
          set({ loading: false, error: err.error || _authErr('verificationFailed') })
          return { success: false, error: err.error }
        }
      },

      // Legacy single-step phone verify — used only by the sign-in "needs
      // verification" path (a real unverified row, no pending token, e.g. an
      // MD-created patient). Deferred new signups use verifyPhone/verifyEmail.
      verify: async (code) => {
        const userId = get().pendingUserId
        if (!userId) {
          return { success: false, error: 'No pending registration' }
        }
        set({ loading: true, error: null })
        try {
          const response = await authAPI.verify({ userId, code })
          set({ loading: false })
          return { success: true, userId: response.userId || userId }
        } catch (err) {
          set({ loading: false, error: err.error || _authErr('verificationFailed') })
          return { success: false, error: err.error }
        }
      },
      
      resendCode: async (channel = 'phone') => {
        const token = get().pendingToken
        if (!token) {
          return { success: false, error: 'No pending registration' }
        }
        set({ loading: true, error: null })
        try {
          const response = await authAPI.resendCode({ pendingToken: token, channel })
          set({
            loading: false,
            pendingToken: response.pendingToken || token,
          })
          // TF-13: surface whether the send actually happened, so Verify.jsx can
          // keep the failure visible instead of silently restarting the timer.
          return { success: response.success !== false, error: response.error || null }
        } catch (err) {
          set({ loading: false, error: err.error || _authErr('failedToResend') })
          return { success: false, error: err.error, code: err.code }
        }
      },
      
      login: async (email, password, pin, code) => {
        set({ loading: true, error: null })
        try {
          const response = await authAPI.login(email, password, pin, code)
          _resetPhiForUser(get().user, response.user)
          // PROD-1203: accepted credentials are a real sign-in: an earlier PIN lock, its return
          // route and a stale away stamp go, and the inactivity clock starts now.
          const signedInAt = Date.now()
          writeIdleStamp({ lastActivityAt: signedInAt, hiddenAt: null })
          clearProfileRunPending() // PROD-1192 (L2a): another user's setup-run marker never carries over
          set({
            isLocked: false, lockedAt: null, lockedRoute: null, pinFailedAttempts: 0,
            awayAt: null, authAt: signedInAt,
            // PROD-943b: an e-mail/password session (the 2FA-code and PIN steps
            // come through here too) confirms account deletion with the password.
            user: response.user ? { ...response.user, signInProvider: 'email' } : response.user,
            isAuthenticated: true, 
            loading: false 
          })
          return { success: true, needs2faSetup: response.needs2faSetup }
        } catch (err) {
          // Check if needs verification or PIN
          if (err.needsVerification) {
            set({ 
              loading: false, 
              pendingUserId: err.userId,
              error: 'Please verify your phone number'
            })
            return { success: false, needsVerification: true, userId: err.userId }
          }
          if (err.needsPin) {
            set({ 
              loading: false,
              pendingUserId: err.userId,
            })
            return { success: false, needsPin: true, needs2faSetup: err.needs2faSetup, userId: err.userId }
          }
          // v145.0.0: 2FA code required
          if (err.needs2faCode) {
            set({ loading: false })
            return { success: false, needs2faCode: true, method: err.method, userId: err.userId }
          }
          // v145.0.0: Needs 2FA setup (no PIN and no 2FA)
          if (err.needs2faSetup) {
            set({ 
              loading: false,
              pendingUserId: err.userId,
            })
            return { success: false, needs2faSetup: true, userId: err.userId }
          }
          // v145.0.0: Account locked
          if (err.locked) {
            set({ loading: false, error: err.error })
            return { success: false, locked: true, remainingSeconds: err.remainingSeconds, error: err.error }
          }
          
          set({ loading: false, error: err.error || _authErr('loginFailed') })
          return { success: false, error: err.error, attemptsRemaining: err.attemptsRemaining, errorType: err.errorType, provider: err.provider }
        } finally {
          // v242.157.0: every branch above already clears `loading`; this makes
          // that an invariant of the function rather than of six return paths.
          // ★ IT DOES NOT FIX THE PERSISTED-`loading` DEADLOCK — a process kill
          //   mid-await runs no JS at all. `partialize` is what fixes that.
          if (get().loading) set({ loading: false })
        }
      },
      
      // Refresh user data (gets latest user info including orellaMemberId)
      refreshUser: async () => {
        try {
          const response = await authAPI.getMe()
          if (response.user) {
            set((state) => ({
              user: { ...state.user, ...response.user }
            }))
            return { success: true }
          }
          return { success: false }
        } catch (err) {
          console.error('[Store] refreshUser error:', err)
          return { success: false, error: err.error }
        }
      },

      // v242.9.0: validate a rehydrated session against the server on boot. The
      // store persists isAuthenticated/user, so without this a deleted account
      // could resurrect a logged-in session with stale 2FA (e.g. after the row
      // is deleted server-side). /auth/me now 401s for deleted accounts — on that
      // (or any explicit auth failure) we log out so the app returns to /welcome
      // and a fresh signup runs full verification. Transient network/timeout
      // errors (status 0 / 408) keep the session and are retried next boot.
      validateSession: async () => {
        // ★★★ v242.169.0 — STALE-SESSION-1. ASK THE TOKEN BEFORE ASKING THE
        //   SERVER. The catch below deliberately KEEPS the session on any
        //   non-401 error, because a timeout must not sign a patient out — and
        //   that is correct only while "we could not ask" is genuinely unknown.
        //   A token whose own `exp` has passed is not unknown; it is decided,
        //   and no network is required to decide it. Without this, an expired
        //   token on a flaky connection returned true here forever.
        if (isTokenExpired(getToken())) {
          get().logout()
          return false
        }
        try {
          const response = await authAPI.getMe()
          if (response?.user) {
            set((state) => ({ user: { ...state.user, ...response.user } }))
            return true
          }
          get().logout()
          return false
        } catch (err) {
          const status = err?.status
          if (status === 401 || err?.errorType === 'account_deleted') {
            get().logout()
            return false
          }
          return true
        }
      },
      
      // Set pending user ID (for resuming verification flow)
      setPendingUser: (userId, phone = null) => {
        set({ pendingUserId: userId, pendingPhone: phone })
      },
      
      // Forgot password flow
      requestPasswordReset: async (phone) => {
        set({ loading: true, error: null })
        try {
          const response = await authAPI.forgotPassword(phone)
          // PROD-903: flags only — the answer carries the reset handle and the masked phone.
          console.log('[Store] requestPasswordReset response:', { success: !!response?.success, smsSent: !!response?.smsSent })
          set({ loading: false })
          return {
            success: true,
            userId: response.userId,
            resetToken: response.resetToken,
            smsSent: response.smsSent,
            maskedPhone: response.maskedPhone,
            phone: response.phone  // Phone for Twilio Verify
          }
        } catch (err) {
          set({ loading: false, error: err.error || _authErr('failedToSendReset') })
          return { success: false, error: err.error, code: err.code }
        }
      },
      
      verifyResetCode: async (userId, code, resetToken, phone) => {
        console.log('[Store] verifyResetCode called with userId:', userId, 'code length:', code?.length)
        set({ loading: true, error: null })
        try {
          const response = await authAPI.verifyResetCode(userId, code, resetToken, phone)
          set({ loading: false })
          return {
            success: true,
            resetAuthToken: response.resetAuthToken
          }
        } catch (err) {
          console.log('[Store] verifyResetCode error:', err)
          set({ loading: false, error: err.error || _authErr('invalidVerification') })
          return { success: false, error: err.error, code: err.code }
        }
      },
      
      resetPassword: async (userId, password, resetAuthToken) => {
        set({ loading: true, error: null })
        try {
          await authAPI.resetPassword(userId, password, resetAuthToken)
          set({ loading: false })
          return { success: true }
        } catch (err) {
          set({ loading: false, error: err.error || _authErr('failedToResetPassword') })
          return { success: false, error: err.error }
        }
      },
      
      resendResetCode: async (userId) => {
        set({ loading: true, error: null })
        try {
          const response = await authAPI.resendResetCode(userId)
          set({ loading: false })
          return {
            success: true,
            smsSent: response.smsSent
          }
        } catch (err) {
          set({ loading: false, error: err.error || _authErr('failedToResendCode') })
          return { success: false, error: err.error, code: err.code }
        }
      },

      // ═══ THE E-MAIL LINK RESET ════════════════════════════════════════════
      // Beside the SMS four above, not instead of them. See services/api.js for
      // why both channels are live and which one is leaving.

      /**
       * Ask for a reset link by e-mail.
       *
       * ★★★ THE OUTCOME IS DELIBERATELY NOT "DID WE FIND THE ACCOUNT". It is
       *   'sent' — meaning the request was accepted — or 'retry', meaning WE
       *   failed. Nothing here reads the response body, because the body is the
       *   same one fixed sentence whether or not an account exists, and reading
       *   it could only ever be an attempt to tell those two apart.
       *
       * ★★ AND NOTHING HERE LOGS. requestPasswordReset above prints its whole
       *   response; on this route the caller's input is an address a stranger may
       *   have typed, and CloudTrail/console is not a place to accumulate those.
       *
       * ★ 'retry' covers 400 (the string is not an address at all — the screen
       *   validates the shape first, so this should be unreachable), 503 (our
       *   lookup failed), 408 (timeout) and 0 (network). Every one of them is a
       *   fact about the request or about us, never about the account.
       */
      requestPasswordResetEmail: async (email) => {
        set({ loading: true, error: null })
        try {
          await authAPI.forgotPasswordEmail(email)
          set({ loading: false })
          return { outcome: 'sent' }
        } catch (err) {
          set({ loading: false })
          return { outcome: 'retry', status: err?.status }
        }
      },

      /**
       * Redeem a reset link. THE ONLY THING THAT SPENDS THE TOKEN.
       *
       * ★★★ THREE OUTCOMES AND NO MORE. 'ok' — the password is set. 'link-dead' —
       *   the server refused the token, and it refuses unknown, malformed,
       *   expired, already-spent, deleted-account and lost-the-race with ONE
       *   sentence and one 400 on purpose, so there is nothing to tell apart.
       *   'retry' — WE could not answer (503, timeout, network); calling a link
       *   dead because our own service was down sends a locked-out patient away
       *   for good over an outage that lasted a minute.
       *
       * ★★ THE SERVER ALSO ANSWERS 400 FOR A PASSWORD UNDER EIGHT CHARACTERS,
       *   which is a fact about the password and not about the token. The screen
       *   enforces the same minimum BEFORE calling, so that 400 is unreachable
       *   from here — which is what lets this map 400 to 'link-dead' without
       *   reading the body. If the screen's check is ever removed, this mapping
       *   becomes wrong; check-patient-email-reset asserts the check is there.
       *
       * ★ THE TOKEN IS NEVER LOGGED, in any branch, including the failures.
       */
      redeemPasswordResetLink: async (token, password) => {
        set({ loading: true, error: null })
        try {
          await authAPI.resetPasswordByLink(token, password)
          set({ loading: false })
          return { outcome: 'ok' }
        } catch (err) {
          set({ loading: false })
          return { outcome: err?.status === 400 ? 'link-dead' : 'retry', status: err?.status }
        }
      },
      
      logout: () => {
        // v242.51.0 (PEN Tier-1): revoke the token server-side before clearing it
        // locally (fire-and-forget; see serverLogout). Covers every logout path —
        // Sign Out, idle timeout, PIN-failure, privacy lock, delete-account.
        serverLogout()
        clearToken()
        // v242.131.0: the scoped 2FA-setup bearer must not survive a logout.
        try { clearSetupToken() } catch (_) {}
        set({ 
          user: null, 
          isAuthenticated: false,
          pendingUserId: null,
          pendingPhone: null,
          pendingProfile: null,
          // v237.6.0: clear session-lock state
          isLocked: false,
          lockedAt: null,
          lockedRoute: null,
          pinFailedAttempts: 0,
          // v242.130.0: the away/auth stamps are session state, not device state.
          awayAt: null,
          authAt: null,
        })
        // PROD-252 (P17 step 10): so is the foreground idle clock.
        clearIdleStamp()
        clearProfileRunPending() // PROD-1192 (L2a): the setup-run marker is session state too
        // Clear cases storage directly (useCaseStore defined later)
        localStorage.removeItem('orella-cases')
        // v242.39.0: also reset the in-memory case + profile stores — logout
        // previously cleared only their storage, so account A's cases/profile
        // lingered in memory and flashed on account B in the same session.
        try { useCaseStore.getState().resetForUser() } catch (_) {}
        try { useProfileStore.getState().resetForUser() } catch (_) {}
        try { useAgentStore.getState().resetForUser() } catch (_) {}
        try { useNotificationStore.getState().resetForUser() } catch (_) {}
        // v242.169.0 PHI-ISO-3 — mirror of _resetPhiForUser; see the note there.
        try { useOnboardingStore.getState().reset() } catch (_) {}
        try { sessionStorage.removeItem('orella_onboarding_draft') } catch (_) {}
        // v242.169.0 TOKEN-KEY-1 — shed the pre-v242.169.0 duplicate JWT. A
        // device that ran an earlier build carries a live bearer that no logout
        // has ever removed; this is the path every logout goes through.
        try { purgeLegacyAuth() } catch (_) {}
        // v242.87.0: clear auth from BOTH storages. `authRemove` deletes the
        // key from sessionStorage AND localStorage, so a token written by a
        // build from before this change cannot survive a logout on a build
        // after it — the upgrade path is the case where a half-clear would
        // leave a live session behind with no UI showing it.
        try { authRemove('orella-user') } catch {}
        try { authRemove('orella-profile') } catch {}
        try { sessionStorage.removeItem('orella-profile') } catch {}
        // PROD-38 repair (N2): the route kept for after a sign-in belongs to this session. A
        // sign-out drops it, so the next person who signs in on this tab lands on /home, not on
        // the previous person's route.
        try { sessionStorage.removeItem(POST_LOGIN_KEY) } catch {}
      },
      
      clearError: () => set({ error: null }),
    }),
    { 
      name: 'orella-user',
      // v242.157.0 — USERSTORE-TRANSIENT-PERSISTED-1: only the allow-list is
      // written. Adding a key to the store without listing it above fails
      // `check-store-persistence.mjs`.
      partialize: (state) =>
        Object.fromEntries(USER_STORE_PERSISTED_KEYS.map((k) => [k, state[k]])),
      // ★★★ AND THE UPGRADE PATH, WHICH IS THE HALF THAT IS EASY TO MISS.
      //   `partialize` only governs what this build WRITES. Every device that
      //   ran a build before this one already has `error` and `loading` sitting
      //   in its stored blob, and zustand merges the stored object over the
      //   initial state on rehydrate — so without this the fix would work only
      //   on a fresh install and the reported banner would survive the upgrade
      //   that was meant to remove it. Strip transients on the way IN too.
      merge: (persisted, current) => {
        const clean = { ...(persisted || {}) }
        for (const k of USER_STORE_TRANSIENT_KEYS) delete clean[k]
        // ★★★ v242.169.0 — STALE-SESSION-1. THE ONLY PLACE THAT RUNS BEFORE THE
        //   FIRST PAINT. `merge` is called synchronously while the store is
        //   being created, which is earlier than any effect, any route guard and
        //   any render — so a session that is already provably dead never
        //   reaches the screen as a live one. validateSession (one round trip,
        //   once per boot) still runs and remains the authority; this only
        //   forecloses the two cases the client can settle on its own:
        //
        //     · the credential is GONE while the blob still says signed in
        //       (an interrupted logout, or a store cleared underneath us), and
        //     · the credential states an `exp` that has already passed.
        //
        //   ★★ AN UNREADABLE TOKEN IS NEITHER OF THOSE and is left alone — see
        //      utils/authToken.js. "I cannot tell" must not sign anyone out.
        //   ★ No logout() call here: this runs during module initialisation, and
        //     logout() fires a network request and reaches into four other
        //     stores that may not exist yet. Dropping the flags and clearing the
        //     key is the whole of what is needed; the next boot is idempotent.
        if (clean.isAuthenticated === true) {
          let token = null
          try { token = getToken() } catch (_) { token = null }
          const dead = !token || isTokenExpired(token)
          if (dead) {
            if (token) { try { clearToken() } catch (_) {} }
            clean.isAuthenticated = false
            clean.user = null
            clean.isLocked = false
            clean.lockedAt = null
            clean.lockedRoute = null
          }
        }
        return { ...current, ...clean }
      },
      // ★★★ v242.87.0 (TF b34 #13/#17) — see src/utils/authStorage.js. This was
      //   sessionStorage on the reasoning that auth should die with the browser
      //   tab; in a native app there is no tab, and what it actually died with
      //   was the WKWebView content process — which iOS recycles on memory
      //   pressure and on returning from the share sheet. Two reports of "it
      //   took me to the homepage" were this, not the buttons they blamed.
      storage: {
        getItem: (name) => {
          const str = authGet(name)
          if (!str) return null
          try { return JSON.parse(str) } catch { return null }
        },
        setItem: (name, value) => {
          try { authSet(name, JSON.stringify(value)) } catch {}
        },
        removeItem: (name) => authRemove(name),
      },
    }
  )
)

export const useProfileStore = create(
  persist(
    scrubProfileWrites((set, get) => ({
      profile: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        profilePhoto: null,
        // Profile sections from API
        identification: {},
        insurance: {},
        allergies: {},
        medications: {},
        medicalHistory: {},
        familyHistory: {},
        socialLifestyle: {},
        preferredPharmacy: null,
      },
      loading: false,
      syncing: false,
      // PROD-306: the discovery result (by its recheckedAt/ranAt stamp) whose Home toast the
      // patient closed this session. Transient: never persisted, so the toast returns on the
      // next app open; a newer result has another stamp and is not hidden by it.
      discoveryToastSnoozedFor: null,

      // v242.39.0: full profile reset for account switch / logout — mirror of the
      // case store's resetForUser so account A's profile can't flash on account B.
      resetForUser: () => {
        set({
          profile: {
            firstName: '', lastName: '', email: '', phone: '', profilePhoto: null,
            identification: {}, insurance: {}, allergies: {}, medications: {},
            medicalHistory: {}, familyHistory: {}, socialLifestyle: {}, preferredPharmacy: null,
          },
          loading: false, syncing: false,
        })
        try { sessionStorage.removeItem('orella-profile') } catch (_) {}
      },
      
      // Load profile from API - only merge non-empty data
      loadProfile: async () => {
        set({ loading: true })
        try {
          const { profileAPI } = await import('../services/api')
          const apiProfile = await profileAPI.get()
          if (apiProfile) {
            const currentProfile = get().profile
            
            // Only update sections that have actual data from API
            // Don't overwrite local data with empty objects
            const mergedProfile = { ...currentProfile }

            // ★★★ v242.130.0 — A SECTION MAY ARRIVE AS A STRING. The API's
            //     _sync_medications_to_profile wrote `medications` through
            //     json.dumps, so the JSONB column held a JSON *string*, and this
            //     block assigned it straight through. The old length guard,
            //     `Object.keys(section).length > 0`, returns CHARACTER INDICES for a
            //     string — so it passed on precisely the input it should have
            //     caught, and `p.medications?.completed` came back undefined.
            //     The writer is fixed in API v243.138.0; this is the reader half,
            //     and it repairs already-corrupted rows with no data migration.
            // ★★ One parser, one key map — see utils/profileSections.js. Adding a
            //    section means adding it THERE, not another `if` here.
            for (const [rowKey, storeKey] of Object.entries(PROFILE_SECTION_KEYS)) {
              if (hasSectionContent(apiProfile[rowKey])) {
                mergedProfile[storeKey] = parseSection(apiProfile[rowKey])
              }
            }
            if (apiProfile.profile_photo) {
              mergedProfile.profilePhoto = apiProfile.profile_photo
            }
            if (apiProfile.preferred_pharmacy) {
              try {
                mergedProfile.preferredPharmacy = typeof apiProfile.preferred_pharmacy === 'string' 
                  ? JSON.parse(apiProfile.preferred_pharmacy) 
                  : apiProfile.preferred_pharmacy
              } catch { mergedProfile.preferredPharmacy = apiProfile.preferred_pharmacy }
            }

            // v213.5 (C-A-OB-1): Surface the onboarding gate flag.
            // Existing users were backfilled to TRUE in the migration, so
            // only brand-new accounts will see this as undefined/false and
            // be routed into /onboarding.
            mergedProfile.onboardingCompleted = apiProfile.onboarding_completed === true
            // v228.10.0: per-section resume marker (0 when the column/flow is new).
            mergedProfile.onboardingStep = Number(apiProfile.onboarding_step) || 0
            mergedProfile.onboardingLoaded = true
            
            mergedProfile.profileLoaded = true
            set({ profile: mergedProfile, loading: false })
          } else {
            // v213.5 (C-A-OB-1): No profile row yet → brand-new user → not onboarded.
            // Mark loaded so the route gate stops waiting and sends them to /onboarding.
            // ★ No profile row yet: loaded, and truthfully empty. `profileLoaded`
            //   is TRUE here — we asked and the answer was "nothing yet" — which
            //   is a different fact from "we could not ask".
            set((state) => ({
              profile: { ...state.profile, onboardingCompleted: false, onboardingLoaded: true, profileLoaded: true },
              loading: false
            }))
          }
        } catch (err) {
          console.error('Failed to load profile:', err)
          // ★★★ v242.145.0 PROFILE-LOAD-ONESHOT-1. This used to set
          //     onboardingLoaded:true on an EMPTY profile — and
          //     `!profile?.onboardingLoaded` in App.jsx is the ONLY condition
          //     that triggers a retry. So the error path satisfied the only
          //     thing that could have recovered from it: one transient 401 or
          //     timeout at boot and the profile stayed empty for the whole
          //     session, silently, with every document generated after it
          //     carrying a blank facesheet. The two flags are now separate:
          //     `onboardingLoaded` releases the route gate (it must, or the app
          //     hangs), `profileLoaded` says whether we actually have the data,
          //     and only the second one governs a retry.
          // v242.8.3: still resolve the onboarding gate flag on error so the
          // protected-route hold (which waits on onboardingLoaded) can't hang.
          // onboardingCompleted stays as-is (undefined ≠ false) → falls through
          // to the normal screen, matching the prior error fallback.
          set((state) => ({
            profile: { ...state.profile, onboardingLoaded: true, profileLoaded: false },
            loading: false,
          }))
        }
      },
      
      // Update profile locally
      updateProfile: (updates) => {
        // P17 (Q12): this used to log `updates` and the whole new profile, which put
        //   insurance and ID data in the browser console. It logs nothing now.
        set((state) => {
          // PROD-237 (P17 step 7): a card section written locally (ProfileInsurance's offline
          //   fallback, the eligibility write-backs) is slimmed like a save, so no base64 card
          //   reaches the persisted profile; an unsaved card keeps the stored one.
          const slimmed = { ...updates }
          for (const section of ['insurance', 'identification']) {
            if (updates && section in updates) slimmed[section] = slimProfileSection(section, updates[section], state.profile?.[section]).local
          }
          const newProfile = { ...state.profile, ...slimmed }
          return { profile: newProfile }
        })
      },
      
      // Sync a specific section to API
      syncSection: async (section, data) => {
        set({ syncing: true })
        try {
          // PROD-237 (P17 step 7): the Profile screens hand over the card as captured, base64
          //   and all. `body` is what PUT /profile carries (an unsaved card is left out, so the
          //   server keeps the stored one); `local` is what this store keeps.
          const { body, local } = slimProfileSection(section, data, get().profile?.[section])
          const { profileAPI } = await import('../services/api')
          
          // Map frontend keys to API keys
          const keyMap = {
            'medicalHistory': 'medicalHistory',
            'familyHistory': 'familyHistory', 
            'socialLifestyle': 'socialLifestyle',
          }
          
          const updateData = { [keyMap[section] || section]: body }
          const resp = await profileAPI.updateReportingRefusal(updateData)
          // ★ TEST 1.02 R2 — the server may have REFUSED an ID card in this save (its name is
          //   not the account's). The local copy must then not claim the card was kept, so
          //   the profile is re-read from the server instead of trusted from `data`.
          const idDocumentRefused = !!(resp && resp.idDocumentRefused)
          // ★ PROD-431 (2), patient push 3a: the server's answer to an insurance save carries the
          //   payer keys ensure_stedi_payer_id just wrote (an ambiguous_region record's candidates
          //   among them). The store kept only what it SENT, so the region card showed late, after
          //   some later reload. The payer keys of the stored row win; everything else is ours.
          const stored = section === 'insurance' && resp && resp.profile && resp.profile.insurance
          const kept = stored && typeof stored === 'object' ? { ...local, ...payerKeysForWire(stored) } : local
          
          // ★ U2-INS C5 (TEST 3.09): orella-api shallow-merges the insurance section
          //   (_shallow_merge_section): a key the body did not carry is KEPT. The store mirrors
          //   that, so a save of the form's keys leaves the server's own (last_eligibility,
          //   discovery_result, secondary, ...) in place here too. It replaced the section, so
          //   Profile → Insurance's autosave would have dropped the eligibility badge and the
          //   secondary card 1.5 s after the first edit.
          set((state) => ({
            profile: { ...state.profile, [section]: (section === 'insurance' && state.profile?.insurance && typeof state.profile.insurance === 'object' && kept && typeof kept === 'object') ? { ...state.profile.insurance, ...kept } : kept, lastUpdated: new Date().toISOString() },
            syncing: false
          }))
          if (idDocumentRefused) {
            try { await get().loadProfile() } catch { /* the refusal is still reported */ }
          }
          // ★ U2 push 2 review r1 M3: the stored row's insurance goes back to the caller, so a
          //   screen that keeps a form (Profile → Insurance) can take the payer keys the server
          //   just wrote into it and stop sending its stale ones on the next save.
          return { success: true, idDocumentRefused, ...(stored && typeof stored === 'object' ? { storedInsurance: stored } : {}) }
        } catch (err) {
          console.error('Failed to sync profile:', err)
          set({ syncing: false })
          
          // Provide more specific error messages
          let errorMessage = err.message || _authErr('unknownError')
          if (err.status === 413) {
            errorMessage = 'Data is too large. Please use smaller images.'
          } else if (err.status === 408) {
            errorMessage = _authErr('requestTimedOut')
          } else if (err.status === 0 || errorMessage.includes('fetch') || errorMessage.includes('network')) {
            errorMessage = _authErr('networkError')
          }
          
          // TEST 2.25 (P5 step 16): the server's refusal code rides along, so a caller can
          // translate dob_under_18 / dob_invalid instead of showing "Unknown error".
          return { success: false, error: errorMessage, ...(err && err.code ? { code: err.code } : {}) }
        }
      },
      
      setProfilePhoto: async (photoUrl) => {
        set((state) => ({
          profile: { ...state.profile, profilePhoto: photoUrl }
        }))
        // Also sync to API
        try {
          const { profileAPI } = await import('../services/api')
          await profileAPI.update({ profilePhoto: photoUrl })
        } catch (err) {
          console.error('Failed to sync profile photo:', err)
        }
      },

      // v222.1.0 Stedi (Seam 2): apply a discovered insurance candidate.
      // Updates profiles.insurance with the candidate's carrier/memberId,
      // marks discovery_result.acknowledged=true and appliedCandidateIndex.
      // Returns { success, error? } so the toast can react.
      applyDiscoveryResult: async (candidateIndex) => {
        const state = get()
        const ins = state.profile?.insurance || {}
        const dr = ins.discovery_result
        if (!dr || !Array.isArray(dr.candidates) || !dr.candidates[candidateIndex]) {
          return { success: false, error: 'Candidate not found' }
        }
        const cand = dr.candidates[candidateIndex]

        // Build the new insurance section.
        // ★ PROD-187 (P12): through the owner. payerId is the candidate's own, stediPayerId
        //   and primaryPayerId are cleared, and providerId is the candidate's registry slug
        //   or 'custom'. This read `cand.carrierId || ins.providerId`, so a candidate with no
        //   slug kept the OLD payer's routable id, and cand.payerId was never read at all.
        const updated = insuranceFromDiscoveryCandidate(ins, cand, candidateIndex, new Date().toISOString())

        // Optimistic local update
        set((s) => ({ profile: { ...s.profile, insurance: updated } }))

        // Persist
        // ★ PROD-482 (the client half of PROD-418's lost update): only the keys this apply
        //   CHANGED are sent. PUT /profile shallow-merges insurance, and this sent the store's
        //   whole section, as loaded whenever the app last read it: a key written since by
        //   anyone else (an MA's member ID from the md panel, another device) was put back to the
        //   store's old copy. orella-api's own discovery write-back stopped doing that in D1
        //   (v243.269.0, write only the discovery keys).
        const changed = Object.fromEntries(Object.entries(updated).filter(([k, v]) => !(k in ins) || ins[k] !== v))
        try {
          const { profileAPI } = await import('../services/api')
          await profileAPI.update({ insurance: changed })
          return { success: true, applied: cand }
        } catch (err) {
          console.error('[applyDiscoveryResult] sync failed:', err)
          return { success: false, error: err?.message || 'Sync failed' }
        }
      },

      // v238.11.0 (S17-4 COB): apply a SECOND discovered candidate as the
      // patient's secondary policy. Mirrors applyDiscoveryResult but routes to
      // profiles.insurance.secondary via the COB endpoint (which also stamps the
      // Stedi payer ID and fires a silent secondary eligibility check).
      applyDiscoverySecondary: async (candidateIndex, userId) => {
        const state = get()
        const ins = state.profile?.insurance || {}
        const dr = ins.discovery_result
        if (!dr || !Array.isArray(dr.candidates) || !dr.candidates[candidateIndex]) {
          return { success: false, error: 'Candidate not found' }
        }
        if (!userId) return { success: false, error: 'Missing user id' }
        const cand = dr.candidates[candidateIndex]
        // ★ INSURE-2 (P12): a candidate with no member ID is never POSTed. The server reads
        //   memberId '' as "clear the secondary": it deleted the patient's existing secondary
        //   and answered { secondary: null }, and this stored its own local object and showed
        //   success. Only the server's echo of what it stored counts as saved.
        const secondary = buildDiscoveredSecondary(cand)
        if (!secondary) return { success: false, error: 'no_member_id' }
        try {
          const { profileAPI } = await import('../services/api')
          const result = await profileAPI.saveSecondaryInsurance(userId, secondary)
          const saved = secondaryFromResponse(result, secondary)
          if (!saved.ok) return { success: false, error: 'not_saved' }
          const updated = {
            ...ins,
            secondary: saved.secondary,
            // PROD-1133: the old plan's result is only a fallback for the SAME coverage; a changed plan with no answer shows "not checked".
            last_eligibility_secondary: saved.eligibility || (sameSecondaryCoverage(ins.secondary, saved.secondary) ? ins.last_eligibility_secondary : null) || null,
            discovery_result: { ...dr, acknowledged: true, secondaryAppliedIndex: candidateIndex, appliedAt: new Date().toISOString() },
          }
          set((s) => ({ profile: { ...s.profile, insurance: updated } }))
          return { success: true, applied: cand }
        } catch (err) {
          console.error('[applyDiscoverySecondary] sync failed:', err)
          return { success: false, error: err?.message || 'Sync failed' }
        }
      },

      // v222.1.0 Stedi (Seam 2): patient dismissed the discovery toast.
      // Mark acknowledged=true so the toast doesn't re-appear next mount.
      dismissDiscoveryResult: async () => {
        const state = get()
        const ins = state.profile?.insurance || {}
        const dr = ins.discovery_result
        if (!dr) return { success: true }
        const updated = {
          ...ins,
          discovery_result: { ...dr, acknowledged: true, dismissedAt: new Date().toISOString() },
        }
        set((s) => ({ profile: { ...s.profile, insurance: updated } }))
        try {
          const { profileAPI } = await import('../services/api')
          // ★ PROD-482: discovery_result ALONE (the shallow merge keeps every other key as the
          //   server holds it); the whole store section put back keys written since it was read.
          await profileAPI.update({ insurance: { discovery_result: updated.discovery_result } })
        } catch (err) {
          console.warn('[dismissDiscoveryResult] sync failed:', err?.message || err)
        }
        return { success: true }
      },

      // PROD-306: the X on Home's discovery TOAST. The toast never names a second plan, so
      // on a second-plan offer (isSecondPlanOffer) it only snoozes the toast for this session:
      // no acknowledged, no PATCH, the offer stays (as "No, just the one plan" leaves it, P12
      // D6). Every other toast dismiss is exactly dismissDiscoveryResult, as before.
      dismissDiscoveryToast: async () => {
        const state = get()
        const ins = state.profile?.insurance || {}
        const dr = ins.discovery_result
        if (dr && isSecondPlanOffer(dr, ins)) {
          set({ discoveryToastSnoozedFor: discoverySnoozeKey(dr) })
          return { success: true, snoozed: true }
        }
        return get().dismissDiscoveryResult()
      },

      // v242.7.0 (D — periodic recheck): stamp a fresh background discovery
      // result WITHOUT surfacing it unless coverage materially changed. Called
      // by HomeScreen's quarterly recheck effect. `changed` is computed on the
      // client (discoveryChangedFromApplied). When false, the result is marked
      // acknowledged so the nudge stays silent; ranAt is always refreshed so the
      // next recheck won't fire for another window.
      recordDiscoveryRecheck: async (freshDiscovery, changed) => {
        const state = get()
        const ins = state.profile?.insurance || {}
        if (!freshDiscovery) return { success: false }
        const dr = {
          ...freshDiscovery,
          ranAt: freshDiscovery.ranAt || new Date().toISOString(),
          recheckedAt: new Date().toISOString(),
          changedFromApplied: !!changed,
          acknowledged: !changed,
        }
        const updated = { ...ins, discovery_result: dr }
        set((s) => ({ profile: { ...s.profile, insurance: updated } }))
        try {
          const { profileAPI } = await import('../services/api')
          // ★ PROD-482: discovery_result ALONE, as dismissDiscoveryResult above. This runs on its
          //   own when Home opens (the 90-day re-check), so the whole stale section it sent could
          //   revert a member ID changed elsewhere without the patient touching anything.
          await profileAPI.update({ insurance: { discovery_result: dr } })
        } catch (err) {
          console.warn('[recordDiscoveryRecheck] sync failed:', err?.message || err)
        }
        return { success: true, changed: !!changed }
      },
    })),
    { 
      name: 'orella-profile',
      // v242.160.0 — see the key lists at the top of this file. `loading` here is
      // read by App.jsx:166 as an in-flight guard, so persisting it deadlocked
      // the protected-route hold.
      // PROD-616: `profile` is written without credential keys. (The inner line is kept verbatim:
      //   control-store-persistence.py control 12 points at it.)
      partialize: (state) => withoutProfileCredentials(
        Object.fromEntries(PROFILE_STORE_PERSISTED_KEYS.map((k) => [k, state[k]])),
      ),
      // The upgrade path, as in useUserStore: every device that ran Build 52 or
      // earlier already has `loading:true` on disk, and zustand merges the stored
      // object over the initial state. Without this the fix reaches fresh
      // installs only — and the devices that are stuck are precisely the ones
      // that are not fresh.
      merge: (persisted, current) => {
        const clean = { ...(persisted || {}) }
        for (const k of PROFILE_STORE_TRANSIENT_KEYS) delete clean[k]
        // PROD-616: a credential read back from storage never reaches memory.
        return { ...current, ...withoutProfileCredentials(clean) }
      },
      // ★★ PROD-616: version 0 (zustand's default) blobs may carry the sign-up password. A version
      //   bump with a migrate makes zustand REWRITE the blob right after hydration (persist calls
      //   setItem when it migrated), so storage is scrubbed on the first boot of this build, not on
      //   the next profile write. migrate keeps every other field as it is.
      version: 1,
      migrate: (persisted) => withoutProfileCredentials({ ...(persisted || {}) }),
      // ★★★ v242.145.0 PROFILE-STORE-NATIVE-STORAGE-1 — THE UN-MIGRATED HALF OF
      //     v242.87.0. The old adapter hardcoded sessionStorage, citing
      //     "v187.0: HIPAA — profile dies on browser/tab close". That is a WEB
      //     rationale and it still holds on web, which is exactly what
      //     authStorage gives us: sessionStorage in a browser, localStorage in a
      //     native sandbox. authStorage.js already argues the native case for
      //     auth — a native app is its own sandbox, and localStorage survives
      //     the WKWebView content-process recycle that iOS performs WHENEVER THE
      //     APP HANDS CONTROL TO THE SHARE SHEET. Auth was moved for that
      //     reason; the profile was left behind, so after a recycle the app was
      //     still signed in with an empty profile — and the pre-visit hand-off,
      //     whose last step IS the share sheet, printed a facesheet with no
      //     patient on it. authRemove() already clears BOTH stores on logout, so
      //     a value written under the old policy cannot be resurrected.
      storage: {
        getItem: (name) => {
          const str = authGet(name)
          if (!str) return null
          try { return JSON.parse(str) } catch { return null }
        },
        setItem: (name, value) => {
          try { authSet(name, JSON.stringify(value)) } catch {}
        },
        removeItem: (name) => authRemove(name),
      },
    }
  )
)

export const useCaseStore = create(
  persist(
    (set, get) => ({
      cases: [],
      currentCase: null,
      syncing: false,
      casesLoading: true, // v143.4: starts true, set false after first loadCases completes
      casesLoadFailed: false, // PROD-1412: the list answered 503 case_list_unavailable; not persisted (partialize lists the keys written)
      deletedCaseIds: [], // Track deleted case IDs to prevent re-fetching
      
      // Start a new case - creates in API first
      startCase: async (specialtyId, specialtyName, focusAreaId, focusAreaName) => {
        try {
          const { casesAPI } = await import('../services/api')
          
          // Create case in API
          const response = await casesAPI.create(specialtyName, focusAreaName)
          const apiCase = response.case
          
          // Use API case ID for consistency across all panels
          const caseId = apiCase?.id || `local-${Date.now()}`
          // Derive case number from the last 8 chars of the ID for display (matches admin/MD panels)
          const caseNumber = caseId.startsWith('local-') 
            ? `#${Math.floor(10000000 + Math.random() * 90000000)}`
            : `#${caseId.slice(-8).toUpperCase()}`
          
          const newCase = {
            id: caseId,
            caseNumber: caseNumber,
            createdAt: apiCase?.created_at || new Date().toISOString(),
            status: 'draft',
            specialty: { id: specialtyId, name: specialtyName },
            specialtyId,
            specialtyName,
            focusArea: { id: focusAreaId, name: focusAreaName },
            focusAreaId,
            focusAreaName,
            emergencyScreeningPassed: false,
            voiceRecording: null,
            voiceTextInput: '',
            answers: {}, // Start with empty object - answers will be added as user fills them
            describe: {
              completed: false,
              currentStep: 'voice',
            },
            detect: {
              completed: false,
              photos: [],
              videos: [],
              documents: [],
            },
            diagnose: {
              completed: false,
              summary: null,
            },
            appointments: [], // Array to track all appointments
          }
          set({ currentCase: newCase })
          return newCase
        } catch (err) {
          console.error('Failed to create case in API:', err)
          // Fallback to local-only case
          const localId = `local-${Date.now()}`
          const newCase = {
            id: localId,
            caseNumber: `#${Math.floor(10000000 + Math.random() * 90000000)}`,
            createdAt: new Date().toISOString(),
            status: 'draft',
            specialty: { id: specialtyId, name: specialtyName },
            specialtyId,
            specialtyName,
            focusArea: { id: focusAreaId, name: focusAreaName },
            focusAreaId,
            focusAreaName,
            emergencyScreeningPassed: false,
            voiceRecording: null,
            voiceTextInput: '',
            answers: {}, // Start with empty object
            describe: {
              completed: false,
              currentStep: 'voice',
            },
            detect: {
              completed: false,
              photos: [],
              videos: [],
              documents: [],
            },
            diagnose: {
              completed: false,
              summary: null,
            },
          }
          set({ currentCase: newCase })
          return newCase
        }
      },
      
      // Load cases from API
      // PROD-270: every error is swallowed here (the cases already held are returned),
      // EXCEPT a 403 when the caller opts in with { rethrowRefusal: true }. Only the
      // DiagnoseScreen status poll opts in, so it can stop; every other caller is unchanged.
      loadCases: async (opts) => {
        try {
          const { casesAPI } = await import('../services/api')
          const response = await casesAPI.list()
          const apiCases = response.cases || []
          
          // Get list of deleted case IDs to filter them out
          const deletedIds = get().deletedCaseIds || []
          
          console.log('[loadCases] Received', apiCases.length, 'cases from API')
          console.log('[loadCases] Filtering out', deletedIds.length, 'deleted cases')
          
          // Filter out deleted cases
          const filteredApiCases = apiCases.filter(c => !deletedIds.includes(c.id))
          console.log('[loadCases] After filtering:', filteredApiCases.length, 'cases')

          // v224.8.0 (REF accept gate): defensive client-side mirror of the API
          // gate. A referral-spawned case shares the patient's user_id, so it must
          // not surface until the receiving specialist accepts. The API already
          // suppresses awaiting_acceptance/declined/revoked referral cases for
          // patients; this guards against a stale payload or cached row resurrecting
          // one. Reads both snake_case (API) and camelCase (local) for safety.
          const HIDDEN_REF_STATUSES = ['awaiting_acceptance', 'declined', 'revoked']
          const gatedApiCases = filteredApiCases.filter(c => {
            const isReferral = (c.originator_type || c.originatorType) === 'md_referral'
            const refStatus = c.referral_status || c.referralStatus
            return !(isReferral && HIDDEN_REF_STATUSES.includes(refStatus))
          })
          if (gatedApiCases.length !== filteredApiCases.length) {
            console.log('[loadCases] v224.8.0 referral gate: hid',
              filteredApiCases.length - gatedApiCases.length, 'pre-acceptance/declined referral case(s)')
          }
          
          gatedApiCases.forEach(c => {
            console.log('[loadCases] Case', c.id.slice(-8), 'API status:', c.status, 'completed:', !!c.completed_at)
          })
          
          // Helper to parse finalization data from after_visit_summary
          const parseFinalizationData = (avs) => {
            console.log('[loadCases] parseFinalizationData input:', avs ? (typeof avs + ', length: ' + String(avs).length) : 'null')
            if (!avs) return null
            try {
              // Try to parse as JSON (new format with embedded metadata)
              const parsed = JSON.parse(avs)
              if (typeof parsed === 'object' && parsed !== null) {
                console.log('[loadCases] parseFinalizationData parsed keys:', Object.keys(parsed))
                console.log('[loadCases] parseFinalizationData avsContent length:', parsed.avsContent?.length || 0)
                console.log('[loadCases] parseFinalizationData consultNote length:', parsed.consultNote?.length || 0)
                console.log('[loadCases] parseFinalizationData followUp present:', !!parsed.followUp)
                return parsed
              }
            } catch {
              // Not JSON - just a plain string AVS (legacy format)
              console.log('[loadCases] parseFinalizationData not JSON, treating as raw AVS')
            }
            return { avsContent: avs }
          }
          
          // Transform API cases to local format
          const currentState = get()
          const localCasesMap = new Map(currentState.cases.map(c => [c.id, c]))
          
          const transformedCases = gatedApiCases.map(c => {
            // Parse finalization data from after_visit_summary
            const finData = parseFinalizationData(c.after_visit_summary)
            // PROD-957 Part A: finalize date, signer, exam and note from the latest SIGNED visit
            //   (utils/signedVisit.js); the envelope is encounter 1's for the life of the case
            //   once API 957 is live. Legacy cases (no signed row) keep today's chain.
            const vh = visitHeaderOf(c, finData, c.currentEncounter || c.current_encounter || null)
            // PROD-134 gamma: the stored AVS (cases.avs) when the API sends one, else the legacy chain.
            //   PROD-957: read at the signed visit's encounter between visits, never encounter 1's.
            const avsText = readAvsText(c, vh.avsEncounterNo)
            // PROD-1079: diagnosis / treatment plan from the signed visit, never md's draft selections.
            const signedDx = signedDiagnosisOf(c, finData)
            
            // Get local case data if it exists (to preserve local status)
            const localCase = localCasesMap.get(c.id)
            
            // Merge status - API is source of truth for doctor-controlled statuses
            const statusPriority = { 'completed': 7, 'in_follow_up': 7, 'in_observation': 7, 'in_progress': 6, 'checked_in': 5.5, 'scheduled': 5, 'claimed': 4, 'shared': 3, 'ready_to_share': 2.5, 'submitted': 2, 'draft': 1 }
            const apiStatus = c.status || 'draft'
            const localStatus = localCase?.status || 'draft'
            // Always trust API for doctor-set statuses (these cannot be changed by patient)
            // Only prefer local if local is higher AND API isn't a doctor-set status
            const doctorStatuses = ['completed', 'in_follow_up', 'in_observation', 'in_progress', 'checked_in', 'scheduled', 'claimed']
            // v224.7.3: A revoke/withdraw is a patient-driven DOWNGRADE the priority system would
            // otherwise block (a stale local 'shared' outranks api 'ready_to_share'). When the API
            // returns ready_to_share/draft with no share_code, no doctor, and no target doctor, the
            // case was genuinely un-shared — trust the API and let the status regress.
            const apiIsWithdrawn = (apiStatus === 'ready_to_share' || apiStatus === 'draft')
              && !c.share_code && !c.doctor_id && !c.target_doctor_id
            const mergedStatus = apiIsWithdrawn
              ? apiStatus
              : doctorStatuses.includes(apiStatus)
                ? apiStatus
                : ((statusPriority[localStatus] || 0) > (statusPriority[apiStatus] || 0) ? localStatus : apiStatus)
            
            console.log(`[loadCases] Case ${c.id?.slice(-8)} status: api='${apiStatus}', local='${localStatus}', merged='${mergedStatus}'`)
            console.log(`[loadCases] Case ${c.id?.slice(-8)} encounter: num=${c.currentEncounter || c.current_encounter || '?'}, phase=${c.encounterPhase || c.encounter_phase || '?'}, local_phase=${localCase?.encounterPhase || '?'}`)
            
            // Use API-built diagnose object as fallback (it merges direct columns + diagnose_data JSONB)
            const apiDiagnose = c.diagnose || {}
            
            // v207.2.0: Canonicalize IDs against the local questionnaire catalog rather
            // than relying purely on algorithmic derivation. This fixes the resume-from-draft
            // bug where API names like "Globus (Lump in Throat)" derived to "globus-lump-in-
            // throat" but the catalog ID is "globus-lump". The catalog lookup is now the
            // source of truth; derivation is the fallback for any focus area not in the
            // catalog (e.g., during a future API blob update).
            // v224.3.0: For referral-spawned cases, cases.specialty holds the *doctor*
            // specialty (e.g. "Cardiology"), which is not a questionnaire area. The intake
            // area is persisted separately as specialty_area_id (e.g. 'general'). Prefer it
            // for questionnaire resolution; fall back to deriving from c.specialty for
            // patient-initiated cases (unchanged behavior). specialtyName below stays the
            // doctor specialty so the breadcrumb still reads e.g. "Cardiology › General
            // Health Complaint".
            const _derivedSpecialtyId = c.specialty_area_id || c.specialty?.toLowerCase().replace(/\s+&\s+/g, '-').replace(/\s+/g, '-')
            const _derivedFocusAreaId = c.focus_area?.toLowerCase().replace(/[()]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/-$/, '')
            const _canonical = canonicalizeFocusAreaId(c.specialty, c.focus_area, _derivedSpecialtyId, _derivedFocusAreaId)
            return {
              id: c.id,
              // v235.3.0 — carry the encounter authorization cassette (alias set
              // by GET /cases) so the check-in gate can read currentCase.authorization.
              authorization: c.authorization || null,
              // ★ TEST 2.22 (P3-2): the payer status of the physician's prior authorizations
              //   (caseRequests payerStatus / payerReference, D6a) — a minimal projection, never
              //   the documents themselves. [] when the API sends none (today's view).
              priorAuthRequests: priorAuthPayerEntries(c.caseRequests),
              caseNumber: `#${c.id.slice(-8).toUpperCase()}`,
              createdAt: c.created_at,
              status: mergedStatus, // Use merged status instead of just API status
              completedAt: c.completed_at || finData?.finalizedAt,
              specialty: { name: c.specialty },
              focusArea: { name: c.focus_area },
              specialtyId: _canonical.specialtyId,
              specialtyName: c.specialty,
              focusAreaId: _canonical.focusAreaId,
              focusAreaName: c.focus_area,
              emergencyScreeningPassed: true,
              // Voice recording - construct object if URL exists
              voiceRecording: c.voice_recording_url ? {
                audioData: c.voice_recording_url,
                url: c.voice_recording_url,
                duration: c.voice_duration || '0:00',
                mimeType: c.voice_mime_type || 'audio/webm',
                transcript: c.voice_transcript,
              } : null,
              // v187.1: derive hasVoiceNote from API data so badge shows "Recorded" on reload
              hasVoiceNote: !!c.voice_recording_url || !!c.voice_transcript || localCase?.hasVoiceNote === true,
              voiceRecordingDuration: c.voice_duration || localCase?.voiceRecordingDuration || '',
              voiceTextInput: c.voice_text_input || localCase?.voiceTextInput,
              // PROD-184: the server copy carries the question text in a reserved key;
              // local answers never hold it (see utils/questionText.js).
              answers: stripQuestionText(c.questionnaire_responses) || {},
              questionText: mergeQuestionText(readQuestionText(c.questionnaire_responses), localCase?.questionText),
              // v163.1.1: describeComplete should ONLY be true if:
              // 1. API explicitly says describe_complete is true (set by Submit), OR
              // 2. Local case already had describeComplete = true (from Submit action)
              // REMOVED: questionnaire_responses heuristic — having answers ≠ describe complete
              // REMOVED: status heuristic — completed/in_follow_up cases get describeComplete from API
              describeComplete: c.describe_complete === true || 
                localCase?.describeComplete === true,
              describe: { 
                completed: c.describe_complete === true || 
                  localCase?.describe?.completed === true,
                voiceTranscript: c.voice_transcript,
                voiceTextInput: c.voice_text_input || localCase?.describe?.voiceTextInput,
                voiceRecordingUrl: c.voice_recording_url,
              },
              detect: {
                photos: c.detect?.photos || c.photos || [],
                videos: c.detect?.videos || c.videos || [],
                documents: c.detect?.documents || [],  // Patient uploaded documents from detect object
                doctorFindings: c.detect?.doctorFindings || [],  // v142.0.0: MD clinical uploads visible to patient
              },
              // detectComplete should be true if patient has uploaded media OR case is finalized
              // NOTE: 'claimed' status does NOT mean detectComplete - MD may have created case for patient
              detectComplete: (c.photos?.length > 0 || c.detect?.photos?.length > 0 || c.videos?.length > 0 || c.detect?.videos?.length > 0 || c.detect?.documents?.length > 0) ||
                !!(c.detectData?.skipReason || c.detect_data?.skipReason) || // s117: "Nothing to add" persisted (detect_data.skipReason)

                ['completed', 'in_follow_up'].includes(c.status),
              diagnose: {
                summary: c.ai_documentation || apiDiagnose.avsContent || apiDiagnose.consultNote,
                diagnosis: signedDx.diagnosis,
                treatmentPlan: signedDx.treatmentPlan,
                consultNote: vh.consultNote,
                examinationNotes: vh.examinationNotes,
                avsContent: avsText || finData?.avsContent || apiDiagnose.avsContent,
                billingCodes: finData?.billingCodes || c.billing_codes || apiDiagnose.billingCodes || [],
                cptCodes: finData?.cptCodes || c.cpt_codes || apiDiagnose.cptCodes || [],
                completed: c.status === 'completed',
                // Preserve additional diagnose_data fields from API
                clinicalAnalysis: apiDiagnose.clinicalAnalysis,
                selectedDiagnoses: apiDiagnose.selectedDiagnoses || [],
                selectedTreatments: apiDiagnose.selectedTreatments || [],
                vitalsNotes: apiDiagnose.vitalsNotes,
                physicalExamNotes: apiDiagnose.physicalExamNotes,
                procedureNotes: apiDiagnose.procedureNotes,
                consultTranscript: apiDiagnose.consultTranscript,
                consultRecordingUrl: apiDiagnose.consultRecordingUrl,
                consultRecordingDuration: apiDiagnose.consultRecordingDuration,
                followUpExamNotes: apiDiagnose.followUpExamNotes,
                followUpTranscript: apiDiagnose.followUpTranscript,
              },
              shareCode: apiIsWithdrawn ? null : (c.share_code || c.shareCode || localCase?.shareCode || null),
              // v164.3.0: Load adaptive Q&A from API so data survives refresh
              adaptiveQuestions: c.adaptive_questions || c.adaptiveQuestions || localCase?.adaptiveQuestions || null,
              adaptiveAnswers: c.adaptive_answers || c.adaptiveAnswers || localCase?.adaptiveAnswers || null,
              preVisitSummary: c.pre_visit_summary || localCase?.preVisitSummary || null,
              // ★★ v242.92.0 (TF b37 #12) — patient_summary was written by the API
              //   (aiAPI.persistSummary) and returned by GET /cases (select=*,
              //   api/index.py:15480) but NEVER MAPPED HERE, and it is not in the
              //   localStorage whitelist either. So it existed only in-session,
              //   after ViewSummary or Detect had written it. On a fresh launch
              //   straight to Case Home it was undefined — which is why the Detect
              //   card had no examination to name. The data was always there; this
              //   transform was the missing line.
              patientSummary: c.patient_summary || localCase?.patientSummary || null,
              // v242.235.0 (session 96): the "View my summary" lock and the patient's notes to
              //   their physician (utils/describeLock.js, components/PatientNoteCard.jsx).
              summaryLockedAt: c.summary_locked_at || localCase?.summaryLockedAt || null,
              patientNotes: Array.isArray(c.patient_notes) ? c.patient_notes : (localCase?.patientNotes || []),
              deliverState: c.deliver_state || localCase?.deliverState || null,
              // Doctor source — if local says 'orella' (just converted), don't let API overwrite back to 'external'
              doctorSource: (localCase?.doctorSource === 'orella' && c.doctor_source === 'external') ? 'orella' : (c.doctor_source || localCase?.doctorSource || null),
              doctor_source: (localCase?.doctor_source === 'orella' && c.doctor_source === 'external') ? 'orella' : (c.doctor_source || localCase?.doctor_source || null),
              visitPrepAgreed: c.visit_prep_agreed || localCase?.visitPrepAgreed || false,
              // Encounter loop tracking (v120)
              currentEncounter: c.currentEncounter || c.current_encounter || localCase?.currentEncounter || 1,
              encounterPhase: c.encounterPhase || c.encounter_phase || localCase?.encounterPhase || 'describe',
              encounters: c.encounters || localCase?.encounters || [],
              referrals: c.referrals || localCase?.referrals || [],
              afterVisitSummary: avsText || finData?.avsContent || apiDiagnose.avsContent,
              avsContent: avsText || finData?.avsContent || apiDiagnose.avsContent,
              followUp: (() => {
                // Priority: 1. Database follow_up column (may come as followUp or follow_up), 2. finalized_data followUp, 3. local
                // API transforms follow_up to followUp, so check both
                const rawFollowUp = c.followUp || c.follow_up
                // PROD-903: presence / type / field names only — follow_up is the clinical plan and can hold a visit link.
                console.log('[loadCases] Case', c.id?.slice(-8), 'follow_up raw:', !!rawFollowUp, 'type:', typeof rawFollowUp)
                
                let parsedFollowUp = null
                if (rawFollowUp) {
                  try {
                    parsedFollowUp = typeof rawFollowUp === 'string' 
                      ? JSON.parse(rawFollowUp) 
                      : rawFollowUp
                    console.log('[loadCases] Case', c.id?.slice(-8), 'follow_up parsed keys:', Object.keys(parsedFollowUp || {}))
                  } catch (e) { 
                    console.log('[loadCases] Case', c.id?.slice(-8), 'follow_up parse error:', e?.name)
                    parsedFollowUp = rawFollowUp 
                  }
                }
                
                // Fall back to finalized_data or local
                if (!parsedFollowUp) {
                  parsedFollowUp = finData?.followUp || localCase?.followUp || null
                  console.log('[loadCases] Case', c.id?.slice(-8), 'follow_up fallback:', !!parsedFollowUp)
                }
                
                // Enrich with followUpAppointment data from API if available
                // v121.8.0: API now resolves followUpAppointment by current_encounter number,
                // so stale appointments from previous encounters won't appear here.
                // Only enrich when the appointment is actually confirmed/scheduled (not pending).
                // v172.0.0: Also verify encounter_number matches current to prevent stale enrichment
                const followUpAppt = c.followUpAppointment
                const currentEnc = c.currentEncounter || c.current_encounter || 1
                const apptMatchesEncounter = !followUpAppt?.encounter_number || followUpAppt.encounter_number === currentEnc
                // ★ PROD-921 (review r_921 S1-1): a pending_reschedule row is still enriched, as on
                //   ca0df1ec. appointmentScheduled feeds step logic (DiagnoseScreen followUpCompleted /
                //   hasScheduledFollowUp, DeliverScreen followUpHasAppt), and only labels may change
                //   for this status. The Pending wording is CaseHome followUpAwaitsReschedule and
                //   DeliverScreen isPending (check-pending-reschedule PR-2 holds this).
                const apptNotStale = followUpAppt?.status && !['pending', 'cancelled', 'completed', 'frozen'].includes(followUpAppt.status)
                if (followUpAppt && parsedFollowUp && apptNotStale && apptMatchesEncounter) {
                  console.log('[loadCases] Case', c.id?.slice(-8), 'enriching with followUpAppointment (status:', followUpAppt.status, ', enc:', followUpAppt.encounter_number, ', current:', currentEnc, ')')
                  parsedFollowUp = {
                    ...parsedFollowUp,
                    appointmentScheduled: true,
                    appointmentStatus: followUpAppt.status,
                    scheduledDate: followUpAppt.date || parsedFollowUp.scheduledDate,
                    scheduledTime: followUpAppt.time || parsedFollowUp.scheduledTime,
                    visitType: followUpAppt.type || followUpAppt.appointment_type || parsedFollowUp.visitType
                  }
                } else if (followUpAppt) {
                  console.log('[loadCases] Case', c.id?.slice(-8), 'followUpAppointment skipped — status=', followUpAppt.status, ', enc=', followUpAppt.encounter_number, ', current=', currentEnc, apptMatchesEncounter ? '(status filter)' : '(encounter mismatch)')
                }
                
                return parsedFollowUp
              })(), // Parse follow_up JSON - prioritize database column
              // Parse finalized_by - it may come as JSON string from API
              //   PROD-957: both from the latest signed visit (utils/signedVisit.js visitHeaderOf).
              finalizedBy: vh.finalizedBy,
              finalizedAt: vh.finalizedAt,
              // Track sent to doctor info
              sentToDoctor: c.target_doctor_id || c.doctor_id,
              // ★ PROD-1020: the physician a reactivated case came from (an opaque id); its rows are history.
              previousDoctorId: c.previous_doctor_id || null,
              // ★ PROD-416: a physician HOLDS it (doctor_id set; a case only sent, target_doctor_id
              //   alone, is not held). A held draft offers no Delete (utils/caseDelete.js).
              heldByDoctor: !!c.doctor_id,
              // ★★ TEST 3.18 (patient 242.231.0): a case the SERVER released (a physician declined
              //   the offer, or it was withdrawn elsewhere) keeps no physician from the local copy.
              //   Before, the cached name, card and doctor survived here, so the declined case read
              //   'with_doctor' and offered no "Share with a doctor". Gate: check-case-declined CD-3.
              sentToDoctorName: apiIsWithdrawn ? null : (c.sentToDoctorName || (c.doctor ? clinicianName(withCaseClinician(c.doctor, c), 'display', 'en') : localCase?.sentToDoctorName)),
              sentToDoctorAt: c.shared_at,
              sentToDoctorData: apiIsWithdrawn ? null : (localCase?.sentToDoctorData || null),
              doctor: apiIsWithdrawn ? null : (c.doctor || localCase?.doctor),
              isShared: apiIsWithdrawn ? false : (mergedStatus === 'shared' || mergedStatus === 'claimed' || mergedStatus === 'scheduled' || mergedStatus === 'completed' || !!c.share_code || !!c.target_doctor_id || localCase?.isShared),
              // Appointment data (if available)
              // v199.7: Null out for withdrawn cases (ready_to_share/draft with no doctor)
              appointment: (mergedStatus === 'ready_to_share' || mergedStatus === 'draft') && !c.doctor_id && !c.share_code ? null : (c.appointment || null),
              // ★ PROD-983: after a decline the local (optimistic) appointment is stale; only the server's.
              scheduledAppointment: (mergedStatus === 'ready_to_share' || mergedStatus === 'draft') && !c.doctor_id && !c.share_code ? null
                : caseHasDecline(c) ? (c.appointment || null)
                : (localCase?.scheduledAppointment || c.appointment || null),
              // Parse appointment_request from API (API sends as appointmentRequest after transformation)
              // v199.7: Skip if case is ready_to_share/draft — stale JSONB data shouldn't show after withdrawal
              appointmentRequest: (() => {
                if ((mergedStatus === 'ready_to_share' || mergedStatus === 'draft') && !c.share_code) {
                  return null
                }
                // API sends as appointmentRequest (camelCase) after transformation
                const apiReq = c.appointmentRequest || c.appointment_request
                if (apiReq) {
                  try {
                    const parsed = typeof apiReq === 'string' ? JSON.parse(apiReq) : apiReq
                    console.log('[loadCases] Case', c.id?.slice(-8), 'appointmentRequest from API:', parsed?.status)
                    return parsed
                  } catch { return apiReq }
                }
                return localCase?.appointmentRequest || null
              })(),
              // Derive appointmentStatus from appointmentRequest.status if appointment_status column doesn't exist
              appointmentStatus: (() => {
                if ((mergedStatus === 'ready_to_share' || mergedStatus === 'draft') && !c.share_code) {
                  return null
                }
                if (c.appointment_status) return c.appointment_status
                const apiReq = c.appointmentRequest || c.appointment_request
                if (apiReq) {
                  try {
                    const req = typeof apiReq === 'string' ? JSON.parse(apiReq) : apiReq
                    return req?.status || null
                  } catch { return null }
                }
                return localCase?.appointmentStatus || null
              })(),
              // Appointments array - prefer API if available, fall back to local
              appointments: (() => {
                // API now returns full appointments array
                const apiAppointments = c.appointments
                // ★ PROD-983: after a decline the server's list (rejected rows already dropped) is the
                //   truth even when empty; the local fallback below would bring the declined row back.
                if (Array.isArray(apiAppointments) && caseHasDecline(c)) {
                  return apiAppointments.filter(a => a.status !== 'cancelled' && a.status !== 'rejected')
                }
                if (apiAppointments && Array.isArray(apiAppointments) && apiAppointments.length > 0) {
                  // v199.4: Filter out cancelled/rejected appointments
                  const active = apiAppointments.filter(a => a.status !== 'cancelled' && a.status !== 'rejected')
                  if (active.length > 0) {
                    console.log('[loadCases] Case', c.id?.slice(-8), 'appointments from API:', active.length)
                    return active
                  }
                }
                // Fall back to local appointments if API didn't return any
                if (localCase?.appointments?.length > 0) {
                  return localCase.appointments
                }
                // Last resort: construct from single appointment
                if (c.appointment) {
                  return [{ ...c.appointment, status: c.appointment.status || 'confirmed' }]
                }
                return []
              })(),
              // Follow-up appointment record (with real ID from appointments table)
              // ★ PROD-983: after a decline get_cases sends no row for it; the local one is the declined visit.
              followUpAppointment: c.followUpAppointment || (caseHasDecline(c) ? null : localCase?.followUpAppointment) || null,
              // v181.8.3: Check-in data (token, vitals, timestamps) — surfaced from describe_data.checkIn by API
              checkIn: c.checkIn || localCase?.checkIn || null,
              // MD Panel admin documents (authorization, referral) - v142.0.0: read from adminDocuments
              documents: (() => {
                // API now returns adminDocuments from dedicated column
                const apiDocs = c.adminDocuments || c.documents
                if (apiDocs && typeof apiDocs === 'object' && !Array.isArray(apiDocs)) {
                  console.log('[loadCases] Case', c.id?.slice(-8), 'adminDocuments from API:', {
                    authorization: apiDocs.authorization?.length || 0,
                    referral: apiDocs.referral?.length || 0
                  })
                  return apiDocs
                }
                return localCase?.documents || { authorization: [], referral: [] }
              })(),
              // Deliver data (patient task completion: medication survey, labs, imaging uploads)
              deliver: (() => {
                // Parse deliver from API - may come as JSON string
                const apiDeliver = c.deliver
                if (apiDeliver) {
                  try {
                    const parsed = typeof apiDeliver === 'string' ? JSON.parse(apiDeliver) : apiDeliver
                    console.log('[loadCases] Case', c.id?.slice(-8), 'deliver from API:', {
                      medicationSurveyComplete: parsed.medicationSurveyComplete,
                      radiologyComplete: parsed.radiologyComplete,
                      labsComplete: parsed.labsComplete
                    })
                    return parsed
                  } catch { return apiDeliver }
                }
                // Fall back to local case deliver
                return localCase?.deliver || null
              })(),
              // Preferred pharmacy (for prescriptions)
              preferredPharmacy: (() => {
                const apiPharmacy = c.preferredPharmacy || c.preferred_pharmacy
                if (apiPharmacy) {
                  try {
                    return typeof apiPharmacy === 'string' ? JSON.parse(apiPharmacy) : apiPharmacy
                  } catch { return apiPharmacy }
                }
                return localCase?.preferredPharmacy || null
              })(),
              // Patient agreement (signed consent)
              patientAgreement: (() => {
                const apiAgreement = c.patientAgreement || c.patient_agreement
                if (apiAgreement) {
                  try {
                    return typeof apiAgreement === 'string' ? JSON.parse(apiAgreement) : apiAgreement
                  } catch { return apiAgreement }
                }
                return localCase?.patientAgreement || null
              })(),
              // External doctor info (from patient-specified or share flow)
              externalDoctor: (() => {
                const apiDoc = c.externalDoctor || c.external_doctor
                if (apiDoc) {
                  try { return typeof apiDoc === 'string' ? JSON.parse(apiDoc) : apiDoc } catch { return apiDoc }
                }
                return localCase?.externalDoctor || null
              })(),
              externalDoctorNote: (() => {
                const apiNote = c.externalDoctorNote || c.external_doctor_note
                if (apiNote) {
                  try { return typeof apiNote === 'string' ? JSON.parse(apiNote) : apiNote } catch { return apiNote }
                }
                return localCase?.externalDoctorNote || null
              })(),
              // MD Case onboarding complete flag
              onboardingComplete: c.onboarding_complete || c.onboardingComplete || localCase?.onboardingComplete || false,
              // Diagnose acknowledgment - read from dedicated columns FIRST (most reliable), then diagnose_data JSONB, then local
              diagnoseAcknowledged: c.diagnose_acknowledged === true || apiDiagnose.diagnoseAcknowledged === true || c.diagnoseAcknowledged === true || localCase?.diagnoseAcknowledged === true || false,
              diagnoseAcknowledgedAt: c.diagnose_acknowledged_at || apiDiagnose.diagnoseAcknowledgedAt || c.diagnoseAcknowledgedAt || localCase?.diagnoseAcknowledgedAt || null,
              // v211.7: Referral-spawned case awareness
              originatorType: c.originator_type || c.originatorType || localCase?.originatorType || null,
              referralStatus: c.referral_status || c.referralStatus || localCase?.referralStatus || null,
              referralOriginatorName: c.referral_originator_name || c.referralOriginatorName || localCase?.referralOriginatorName || null,
              referralOriginatorPractice: c.referral_originator_practice || c.referralOriginatorPractice || localCase?.referralOriginatorPractice || null,
              referralReason: c.referral_reason || c.referralReason || localCase?.referralReason || null,
              referralId: c.referral_id || c.referralId || localCase?.referralId || null,
              createdBy: c.created_by || c.createdBy || localCase?.createdBy || null,
            }
          })
          
          // Update cases list
          // v163.1: Removed v160.1 guard that kept stale local cases when API returned 0.
          // The guard blocked legitimate "all cases deleted" from being reflected.
          // Auth errors are caught in the try/catch — a 200 with 0 cases is now trusted.
          set({ cases: transformedCases, casesLoading: false, casesLoadFailed: false })
          
          // Also update currentCase if it matches one of the loaded cases
          // IMPORTANT: Preserve local data (answers, voiceTextInput, detect) that hasn't been synced yet
          // currentState already defined above
          if (currentState.currentCase) {
            const updatedCurrentCase = transformedCases.find(c => c.id === currentState.currentCase.id)
            if (updatedCurrentCase) {
              // v224.7.3: detect a server-side revoke/withdraw (updatedCurrentCase is
              // transform-cleaned, so its fields reflect API truth) so the stale local
              // 'shared'/shareCode in the OR-chain below cannot resurrect a withdrawn case.
              const apiIsWithdrawn = (updatedCurrentCase.status === 'ready_to_share' || updatedCurrentCase.status === 'draft')
                && !updatedCurrentCase.shareCode && !updatedCurrentCase.share_code && !updatedCurrentCase.sentToDoctor
              // Determine isShared - true if EITHER source says true
              const mergedIsShared = apiIsWithdrawn ? false : (updatedCurrentCase.isShared || 
                currentState.currentCase.isShared || 
                !!updatedCurrentCase.shareCode || 
                !!updatedCurrentCase.share_code ||
                !!currentState.currentCase.shareCode ||
                !!currentState.currentCase.share_code ||
                updatedCurrentCase.status === 'shared' ||
                updatedCurrentCase.status === 'claimed' ||
                currentState.currentCase.status === 'shared' ||
                currentState.currentCase.status === 'claimed')
              
              // Use the "most progressed" status - ALWAYS trust API for doctor-set statuses
              const statusPriority = { 'completed': 7, 'in_follow_up': 7, 'in_observation': 7, 'in_progress': 6, 'checked_in': 5.5, 'scheduled': 5, 'claimed': 4, 'shared': 3, 'ready_to_share': 2.5, 'submitted': 2, 'draft': 1 }
              const apiStatus = updatedCurrentCase.status || 'draft'
              const localStatus = currentState.currentCase.status || 'draft'
              // Always prefer API status if it's a doctor-set status
              const doctorStatuses = ['completed', 'in_follow_up', 'in_observation', 'in_progress', 'checked_in', 'scheduled', 'claimed']
              const mergedStatus = apiIsWithdrawn
                ? apiStatus
                : doctorStatuses.includes(apiStatus)
                  ? apiStatus
                  : ((statusPriority[localStatus] || 0) > (statusPriority[apiStatus] || 0) ? localStatus : apiStatus)
              
              // Only merge server-sourced fields, preserve local data
              const mergedCase = {
                ...updatedCurrentCase, // Start with API data
                ...currentState.currentCase, // Then overlay local data (preserves answers, voiceTextInput, etc.)
                // Explicitly set these fields correctly - API takes precedence for server-controlled data
                status: mergedStatus,
                shareCode: apiIsWithdrawn ? null : (updatedCurrentCase.shareCode || currentState.currentCase.shareCode),
                share_code: apiIsWithdrawn ? null : (updatedCurrentCase.share_code || currentState.currentCase.share_code),
                isShared: mergedIsShared,
                // ★ PROD-416: who holds the case is the server's fact, never a stale local one.
                heldByDoctor: !!updatedCurrentCase.heldByDoctor,
                // ★ PROD-1020: the previous physician is the server's fact too (the local overlay above would keep a stale one).
                previousDoctorId: updatedCurrentCase.previousDoctorId || null,
                afterVisitSummary: updatedCurrentCase.afterVisitSummary || currentState.currentCase.afterVisitSummary,
                avsContent: updatedCurrentCase.avsContent || currentState.currentCase.avsContent,
                completedAt: updatedCurrentCase.completedAt || currentState.currentCase.completedAt,
                // v214.8.9 (Issue 2): adminDocuments is API source-of-truth.
                // Without this explicit override, the local-state spread on
                // line above wins and a just-uploaded auth/referral isn't
                // visible until a full re-mount (e.g. nav away + back). The
                // patient submit handler writes via casesAPI.update, so the
                // next loadCases() should always reflect server state.
                adminDocuments: updatedCurrentCase.adminDocuments || currentState.currentCase.adminDocuments,
                // ★ TEST 2.22 (P3-2): the payer status is the server's fact; the local copy never
                //   overrides it (the spread above would keep a stale one).
                priorAuthRequests: updatedCurrentCase.priorAuthRequests,
                documents: updatedCurrentCase.documents || currentState.currentCase.documents,
                // Merge followUp - prefer data with appointmentScheduled info
                // ★ PROD-983: a follow_up the SERVER sent replaces the local one even when it carries no
                //   appointmentScheduled / scheduledDate (a declined held reschedule clears both and says
                //   appointmentStatus 'rejected'); the local scheduled fields were put back over it before.
                //   A local scheduled copy fills in only when the server sent no follow_up at all.
                followUp: (() => {
                  const rawApiCase = gatedApiCases.find(x => x.id === currentState.currentCase.id)
                  const serverSentFollowUp = !!(rawApiCase?.followUp || rawApiCase?.follow_up)
                  return mergeFollowUp(updatedCurrentCase.followUp, currentState.currentCase.followUp, serverSentFollowUp)
                })(),
                finalizedBy: updatedCurrentCase.finalizedBy || currentState.currentCase.finalizedBy,
                finalizedAt: updatedCurrentCase.finalizedAt || currentState.currentCase.finalizedAt,
                // Doctor info from API
                // ★★ TEST 3.18: a case the server released keeps no physician from the open case
                //   either (the local overlay above would otherwise restore all four).
                sentToDoctor: apiIsWithdrawn ? null : (updatedCurrentCase.sentToDoctor || currentState.currentCase.sentToDoctor),
                sentToDoctorName: apiIsWithdrawn ? null : (updatedCurrentCase.sentToDoctorName || currentState.currentCase.sentToDoctorName),
                sentToDoctorData: apiIsWithdrawn ? null : (updatedCurrentCase.sentToDoctorData || currentState.currentCase.sentToDoctorData || null),
                doctor: apiIsWithdrawn ? null : (updatedCurrentCase.doctor || currentState.currentCase.doctor),
                // ★★★ s111 (TF b78): the server's summary wins unless the local copy is strictly newer.
                //   The spread above put the LOCAL patientSummary over the server's, so a records
                //   refresh (D514) never reached a case the patient already had open. utils/summaryFreshness.
                patientSummary: newerSummary(updatedCurrentCase.patientSummary, currentState.currentCase.patientSummary),
                preVisitSummary: newerSummary(updatedCurrentCase.preVisitSummary, currentState.currentCase.preVisitSummary),
                // Preserve diagnose data from server if finalized
                diagnose: updatedCurrentCase.diagnose?.completed 
                  ? updatedCurrentCase.diagnose 
                  : { ...currentState.currentCase.diagnose, ...updatedCurrentCase.diagnose },
                // v142.4.0: Detect — API is source of truth, local only used for in-progress uploads
                detect: (() => {
                  const apiDetect = updatedCurrentCase.detect || {}
                  const localDetect = currentState.currentCase.detect || {}
                  const apiHasFiles = (apiDetect.photos?.length > 0 || apiDetect.videos?.length > 0 || apiDetect.documents?.length > 0)
                  const localHasFiles = (localDetect.photos?.length > 0 || localDetect.videos?.length > 0 || localDetect.documents?.length > 0)
                  
                  // If API has files, ALWAYS use API as base (it's the persisted truth)
                  // Only prefer local if local has MORE files than API (user added files locally but hasn't synced yet)
                  let merged
                  const apiFileCount = (apiDetect.photos?.length || 0) + (apiDetect.videos?.length || 0) + (apiDetect.documents?.length || 0)
                  const localFileCount = (localDetect.photos?.length || 0) + (localDetect.videos?.length || 0) + (localDetect.documents?.length || 0)
                  
                  if (localFileCount > apiFileCount) {
                    // Local has more files — user added files that haven't synced yet
                    console.log('[loadCases] Detect: preferring local (more files)', { local: localFileCount, api: apiFileCount })
                    merged = localDetect
                  } else if (apiHasFiles) {
                    // API has files — use API (source of truth after deploy/refresh)
                    merged = apiDetect
                  } else if (localHasFiles) {
                    // Only local has files (edge case: API sync failed previously)
                    merged = localDetect
                  } else {
                    merged = apiDetect
                  }
                  // v142.0.0: Always take doctorFindings from API (MD uploads, patient can't edit)
                  if (apiDetect.doctorFindings?.length > 0) {
                    merged = { ...merged, doctorFindings: apiDetect.doctorFindings }
                  }
                  return merged
                })(),
                // Preserve deliver data from server (from MD Panel finalization)
                deliver: updatedCurrentCase.deliver 
                  ? { ...currentState.currentCase.deliver, ...updatedCurrentCase.deliver }
                  : currentState.currentCase.deliver,
                // Preserve appointment from server
                appointment: updatedCurrentCase.appointment || currentState.currentCase.appointment,
                scheduledAppointment: caseHasDecline(updatedCurrentCase) ? updatedCurrentCase.scheduledAppointment // PROD-983
                  : (updatedCurrentCase.scheduledAppointment || currentState.currentCase.scheduledAppointment),
                // PROD-983: the local overlay above would keep the declined follow-up row.
                ...(caseHasDecline(updatedCurrentCase) ? { followUpAppointment: updatedCurrentCase.followUpAppointment ?? null } : {}),
                // v199.7: If case status is ready_to_share (withdrawn), clear appointment data
                // even if API still returns stale appointmentRequest from JSONB column
                appointmentRequest: (() => {
                  const mergedStatus = updatedCurrentCase.status || currentState.currentCase.status
                  if ((mergedStatus === 'ready_to_share' || mergedStatus === 'draft') && !(updatedCurrentCase.share_code || updatedCurrentCase.shareCode || currentState.currentCase.share_code || currentState.currentCase.shareCode)) {
                    // Case is unshared — no appointment should be shown
                    return null
                  }
                  const apiReq = updatedCurrentCase.appointmentRequest
                  const localReq = currentState.currentCase.appointmentRequest
                  if (apiReq?.status === 'confirmed') {
                    console.log('[loadCases] Using API appointmentRequest (confirmed):', apiReq.status)
                    return apiReq
                  }
                  if (apiReq) {
                    console.log('[loadCases] Using API appointmentRequest:', apiReq.status)
                    return apiReq
                  }
                  return localReq
                })(),
                appointmentStatus: (() => {
                  const mergedStatus = updatedCurrentCase.status || currentState.currentCase.status
                  if ((mergedStatus === 'ready_to_share' || mergedStatus === 'draft') && !(updatedCurrentCase.share_code || updatedCurrentCase.shareCode || currentState.currentCase.share_code || currentState.currentCase.shareCode)) {
                    return null
                  }
                  return updatedCurrentCase.appointmentStatus || currentState.currentCase.appointmentStatus
                })(),
                // Prefer API appointments (includes both initial and follow-up), fall back to local
                appointments: (() => {
                  const apiAppts = updatedCurrentCase.appointments
                  const localAppts = currentState.currentCase.appointments
                  // ★ PROD-983: after a decline the server's list wins even when empty (see the transform).
                  if (Array.isArray(apiAppts) && caseHasDecline(updatedCurrentCase)) return apiAppts
                  // Prefer API if it has appointments
                  if (apiAppts?.length > 0) {
                    console.log('[loadCases] Using API appointments:', apiAppts.length)
                    return apiAppts
                  }
                  // Fall back to local
                  if (localAppts?.length > 0) {
                    return localAppts
                  }
                  return []
                })(),
                // v126.0.0: Prefer API encounters over local — local may be stale/empty from optimistic updates
                encounters: (() => {
                  const apiEncs = updatedCurrentCase.encounters
                  const localEncs = currentState.currentCase.encounters
                  if (apiEncs?.length > 0) {
                    return apiEncs
                  }
                  if (localEncs?.length > 0) {
                    return localEncs
                  }
                  return []
                })(),
                // v126.0.0: Prefer API referrals over local
                referrals: (() => {
                  const apiRefs = updatedCurrentCase.referrals
                  const localRefs = currentState.currentCase.referrals
                  if (apiRefs?.length > 0) return apiRefs
                  if (localRefs?.length > 0) return localRefs
                  return []
                })(),
                // Encounter tracking — always prefer API (server-controlled)
                currentEncounter: updatedCurrentCase.currentEncounter || currentState.currentCase.currentEncounter || 1,
                encounterPhase: updatedCurrentCase.encounterPhase || currentState.currentCase.encounterPhase || '',
                // Preserve diagnose acknowledgment — once true, NEVER reverts (use strict === true)
                diagnoseAcknowledged: updatedCurrentCase.diagnoseAcknowledged === true || currentState.currentCase.diagnoseAcknowledged === true || false,
                diagnoseAcknowledgedAt: updatedCurrentCase.diagnoseAcknowledgedAt || currentState.currentCase.diagnoseAcknowledgedAt || null,
                // v181.8.3: Preserve checkIn data (prefer local for immediate update, API for refresh)
                checkIn: updatedCurrentCase.checkIn || currentState.currentCase.checkIn || null,
              }
              console.log('loadCases - merging currentCase:', {
                apiStatus,
                localStatus,
                mergedStatus,
                apiIsShared: updatedCurrentCase.isShared,
                localIsShared: currentState.currentCase.isShared,
                mergedIsShared,
                apiHasShareCode: !!updatedCurrentCase.shareCode,
                localHasShareCode: !!currentState.currentCase.shareCode,
                apiFollowUp: updatedCurrentCase.followUp ? 'present' : 'null',
                hasRequirements: !!updatedCurrentCase.followUp?.requirements,
                hasOrdersPlaced: !!updatedCurrentCase.followUp?.ordersPlaced,
              })
              set({ currentCase: mergedCase })
            }
          }
          
          return transformedCases
        } catch (err) {
          if (opts?.rethrowRefusal === true && pollRefused(err)) { set({ casesLoading: false }); throw err }
          console.error('Failed to load cases:', err?.name)
          // PROD-1412: a 503 case_list_unavailable keeps the cases already held AND says so; the list shows a
          // notice with a retry. Any other failure behaves as before.
          set({ casesLoading: false, casesLoadFailed: err?.code === 'case_list_unavailable' })
          return get().cases
        }
      },
      
      // Pass emergency screening
      passEmergencyScreening: () => {
        set(state => ({
          currentCase: state.currentCase 
            ? { ...state.currentCase, emergencyScreeningPassed: true }
            : null
        }))
      },
      
      // Save answer for a question
      saveAnswer: (category, questionId, answer) => {
        set(state => {
          if (!state.currentCase) return state
          return {
            currentCase: {
              ...state.currentCase,
              answers: {
                ...state.currentCase.answers,
                [category]: {
                  ...state.currentCase.answers[category],
                  [questionId]: answer,
                },
              },
            },
          }
        })
      },
      
      // Set describe step
      setDescribeStep: (step) => {
        set(state => ({
          currentCase: state.currentCase
            ? { ...state.currentCase, describe: { ...state.currentCase.describe, currentStep: step } }
            : null,
        }))
      },
      
      // Complete describe section
      completeDescribe: () => {
        set(state => ({
          currentCase: state.currentCase
            ? { ...state.currentCase, describe: { ...state.currentCase.describe, completed: true } }
            : null,
        }))
      },
      
      // Save current case to list AND sync to API
      saveCase: async () => {
        const currentCase = get().currentCase
        if (!currentCase) return
        
        // Save locally first
        set(state => ({
          cases: [...state.cases.filter(c => c.id !== currentCase.id), currentCase],
        }))
        
        // Sync to API
        try {
          const { casesAPI } = await import('../services/api')
          await casesAPI.update(currentCase.id, {
            status: currentCase.status,
            questionnaireResponses: withQuestionText(currentCase.answers, currentCase.questionText),
            describeSummary: currentCase.describe?.summary || '',
          })
        } catch (err) {
          console.error('Failed to sync case to API:', err)
        }
      },
      
      // Get case by ID
      getCase: (caseId) => get().cases.find(c => c.id === caseId),
      
      // Clear current case
      clearCurrentCase: () => set({ currentCase: null }),
      
      // Delete a case permanently
      // ★ PROD-416 (the client half of PROD-404): THE SERVER FIRST. This used to record the id in
      //   deletedCaseIds (persisted), swallow any API error ("Don't throw — still remove
      //   locally") and drop the case from the list: a case the server REFUSED to delete (a
      //   physician holds it; PROD-404 answers 404) vanished from the patient's app for good while
      //   it still existed. Now a server case is removed from the list, and its id recorded, only
      //   after the server confirmed the delete; any error leaves both as they were and is
      //   RETHROWN, so the caller says so (utils/caseDelete.js reportDeleteFailure). A local-only
      //   id (local-/case prefixes) is removed locally, as before.
      deleteCase: async (caseId) => {
        console.log('[deleteCase] Deleting case:', caseId)
        const localOnly = caseId.startsWith('local-') || caseId.startsWith('case')
        let apiDeleted = false
        if (!localOnly) {
          const { casesAPI } = await import('../services/api')
          await casesAPI.delete(caseId) // throws on any refusal; nothing below runs then
          apiDeleted = true
          console.log('[deleteCase] ✅ Deleted from API')
        } else {
          console.log('[deleteCase] Local case, skipping API delete')
        }
        
        // Remove from local state, and keep the id out of later fetches
        set(state => {
          const updatedCases = state.cases.filter(c => c.id !== caseId)
          console.log('[deleteCase] Cases after:', updatedCases.length, 'apiDeleted:', apiDeleted)
          return {
            deletedCaseIds: [...(state.deletedCaseIds || []), caseId],
            cases: updatedCases,
            currentCase: state.currentCase?.id === caseId ? null : state.currentCase,
          }
        })
        
        return { apiDeleted }
      },
      
      // Archive a case with reason
      archiveCase: async (caseId, reason) => {
        try {
          const { casesAPI } = await import('../services/api')
          await casesAPI.archive(caseId, reason)
        } catch (err) {
          console.error('Failed to archive case in API:', err)
        }
        // Update local storage
        set(state => ({
          cases: state.cases.map(c => 
            c.id === caseId 
              ? { ...c, status: 'archived', archiveReason: reason, archivedAt: new Date().toISOString() }
              : c
          ),
          currentCase: state.currentCase?.id === caseId 
            ? { ...state.currentCase, status: 'archived', archiveReason: reason, archivedAt: new Date().toISOString() }
            : state.currentCase,
        }))
      },
      
      // v237.20.0: Reactivate an archived case — detaches from the prior doctor
      // and returns it to a pre-share state. The patient must share again.
      reactivateCase: async (caseId) => {
        try {
          const { casesAPI } = await import('../services/api')
          await casesAPI.reactivate(caseId)
        } catch (err) {
          console.error('Failed to reactivate case in API:', err)
          return { success: false, error: err?.message || 'Failed to reactivate case' }
        }
        // ★★ PROD-965: mirror the server's write (orella-api reactivate_case). It nulls
        //   doctor_id, target_doctor_id and the share code and answers no case row, so the
        //   local case is cleared here. Only doctorId / doctor_id used to be cleared, so the
        //   mapped sentToDoctor / sentToDoctorName / heldByDoctor of the prior physician
        //   survived, and ShareScreen read the freed case as "already sent to an Orella
        //   doctor". Gate: scripts/check-reactivate-fresh-case.mjs.
        // ★ PROD-973: the server's write also nulls appointment_request, appointment_status and
        //   appointment_confirmed_at (no client field reads the last one). The request belonged
        //   to the detached physician; kept here, ShareScreen's doctor-sent view showed its old
        //   date as "Appointment requested ... Pending confirmation". appointment and
        //   scheduledAppointment are derived from it and are null for a ready_to_share case with
        //   no physician and no code after a reload too (loadCases). follow_up, the appointments
        //   list and appointment_id are kept, as on the server.
        const reactivated = {
          status: 'ready_to_share',
          doctorId: null, doctor_id: null,
          targetDoctorId: null, target_doctor_id: null,
          sentToDoctor: null, sentToDoctorName: null, sentToDoctorData: null,
          heldByDoctor: false, doctor: null, isShared: false,
          shareCode: null, share_code: null, sharePin: null,
          shareExpiration: null,
          archiveReason: null, archivedAt: null,
          appointmentRequest: null, appointment_request: null,
          appointmentStatus: null, appointment_status: null,
          scheduledAppointment: null, appointment: null,
        }
        set(state => ({
          cases: state.cases.map(c =>
            c.id === caseId
              ? { ...c, ...reactivated, previousDoctorId: c.sentToDoctor || c.doctor_id || c.previousDoctorId || null } // PROD-1020: the server keeps previous_doctor_id
              : c
          ),
          currentCase: state.currentCase?.id === caseId
            ? { ...state.currentCase, ...reactivated, previousDoctorId: state.currentCase.sentToDoctor || state.currentCase.doctor_id || state.currentCase.previousDoctorId || null }
            : state.currentCase,
        }))
        return { success: true }
      },

      // Resume a case
      resumeCase: (caseId) => {
        const found = get().cases.find(c => c.id === caseId)
        if (found) set({ currentCase: found })
      },
      
      // Update deliver task completion status
      updateDeliverTask: async (taskType, data) => {
        const currentCase = get().currentCase
        if (!currentCase) return { success: false, error: 'No current case' }
        
        console.log('[updateDeliverTask] taskType:', taskType)
        // PROD-903: field names only — the task data and `deliver` hold the patient's survey answers.
        console.log('[updateDeliverTask] data keys:', Object.keys(data || {}))

        const deliver = currentCase.deliver || {}
        console.log('[updateDeliverTask] existing deliver keys:', Object.keys(deliver))
        
        // ★★ PROD-1104: the API gets ONLY what this call changes (`patch`, built from `data`); the
        //   page-load copy of `deliver` is never resent. The LOCAL case is that copy with the patch
        //   laid over it, and a file list is the union by identity (PROD-1103), so the screen keeps
        //   every earlier entry after a write.
        const patch = buildDeliverPatch(taskType, data)
        const removeIds = Array.isArray(data?.removeFiles) ? data.removeFiles.filter(x => typeof x === 'string' && x) : []
        const localDeliver = { ...deliver, ...patch }
        if (taskType === 'radiology' || taskType === 'imaging') {
          localDeliver.radiologyFiles = mergeDeliverFileList(deliver.radiologyFiles, patch.radiologyFiles, removeIds)
        } else if (taskType === 'labs') {
          localDeliver.labsFiles = mergeDeliverFileList(deliver.labsFiles, patch.labsFiles, removeIds)
        }
        const updates = { deliver: localDeliver }
        if (taskType === 'medication') console.log('[updateDeliverTask] medication survey updates:', Object.keys(updates))
        else console.log(`[updateDeliverTask] ${taskType} updates:`, Object.keys(patch))

        // Update locally first
        set(state => ({
          currentCase: { ...state.currentCase, ...updates },
          cases: state.cases.map(c => 
            c.id === currentCase.id ? { ...c, ...updates } : c
          )
        }))
        
        // Sync to API
        try {
          const { casesAPI } = await import('../services/api')
          // ★★ PROD-1042: the API merges deliver file lists per entry and never infers a patient
          //   removal from a list that omits an entry. A delete is sent explicitly as
          //   deliver._remove = { <listName>: [identity, ...] } (identity = id, else storagePath).
          //   It rides on the API payload ONLY: it is never put into the local case, so the next
          //   write does not resend it.
          let apiUpdates = { deliver: patch }
          if (removeIds.length && (taskType === 'radiology' || taskType === 'imaging' || taskType === 'labs')) {
            const listName = taskType === 'labs' ? 'labsFiles' : 'radiologyFiles'
            apiUpdates = { deliver: { ...patch, _remove: { [listName]: removeIds } } }
          }
          await casesAPI.update(currentCase.id, apiUpdates)
          console.log('[updateDeliverTask] Synced to API:', taskType)
          return { success: true }
        } catch (err) {
          console.error('[updateDeliverTask] Failed to sync:', err)
          return { success: false, error: err.message }
        }
      },
      
      // Set current case directly with case object
      setCurrentCase: (caseData) => {
        if (typeof caseData === 'string') {
          const found = get().cases.find(c => c.id === caseData)
          set({ currentCase: found || null })
        } else {
          set({ currentCase: caseData })
        }
      },
      
      // Set current case AND save to cases list atomically
      setAndSaveCase: (caseData) => {
        set(state => ({
          currentCase: caseData,
          cases: [...state.cases.filter(c => c.id !== caseData.id), caseData],
        }))
      },
      
      // Helper to sanitize data before storing (removes DOM elements, React internals)
      sanitizeData: (data, depth = 0) => {
        const indent = '  '.repeat(depth)
        if (data === null || data === undefined) return data
        if (typeof data !== 'object') return data
        if (data instanceof Date) return data.toISOString()
        if (data instanceof File || data instanceof Blob) return null
        if (data instanceof HTMLElement || data instanceof Event) return null
        if (data.$$typeof || data._reactInternals || data.nodeType) return null
        
        if (Array.isArray(data)) {
          return data.map(item => get().sanitizeData(item, depth + 1)).filter(item => item !== null)
        }
        
        const sanitized = {}
        for (const [key, value] of Object.entries(data)) {
          // Skip React/internal properties
          if (key.startsWith('_') || key.startsWith('$') || 
              key === 'target' || key === 'currentTarget' ||
              key === 'nativeEvent' || key === 'view' ||
              key.includes('reactFiber') || key.includes('__react')) {
            continue
          }
          if (typeof value === 'function') continue
          
          const sanitizedValue = get().sanitizeData(value, depth + 1)
          if (sanitizedValue !== null && sanitizedValue !== undefined) {
            sanitized[key] = sanitizedValue
          }
        }
        
        const result = Object.keys(sanitized).length > 0 ? sanitized : null
        if (depth === 0) {
          console.log('sanitizeData final result:', result ? Object.keys(result) : 'null')
        }
        return result
      },
      
      // Update current case with partial data (sanitized)
      // Preserves 'completed' status (immutable after finalization)
      // Allows 'claimed' -> 'scheduled' transition when patient schedules follow-up
      updateCase: (updates) => {
        // ★ PROD-922: sanitizeData returns null for an update whose every value it drops (and for an
        //   object that is empty), and the old code then threw on `sanitized.status`. It also turned an
        //   explicitly EMPTY prefillKeys into null, so `updateCase({ prefillKeys: {} })` (the last prefill
        //   tag removed) never reached the store and the stale tags stayed. An explicit empty prefillKeys
        //   is now written; an update with nothing left to write is a no-op (no write, no throw).
        const sanitized = get().sanitizeData(updates) || {}
        if (updates && updates.prefillKeys && typeof updates.prefillKeys === 'object' && !sanitized.prefillKeys) {
          sanitized.prefillKeys = {}
        }
        if (Object.keys(sanitized).length === 0) return
        set(state => {
          if (!state.currentCase) return { currentCase: null }
          
          // Only preserve 'completed' status (immutable)
          // Allow 'claimed' -> 'scheduled' transition
          const currentStatus = state.currentCase.status
          const newStatus = currentStatus === 'completed' 
            ? 'completed' 
            : (sanitized.status || currentStatus)
          
          // v220.9.3 FIX 3 — preserve detectPrep through patientSummary overwrites.
          //
          // ViewSummary.onComplete, the SSE fallback, DescribeAdaptive,
          // DescribeReview, and CaseTimeline all call:
          //   updateCase({ patientSummary: result, patient_summary: result })
          // with a `result` that contains the streamed differential but NOT
          // the `detectPrep` key that the detect-prep endpoint produces. If
          // detect-prep already ran (e.g., the patient bounced into Detect
          // and back), the local store has a detectPrep — and these later
          // patientSummary writes silently clobber it. Net symptom: Detect's
          // three zones render briefly, then disappear on the next describe
          // recompute. Carrying detectPrep forward when the new summary
          // lacks it is the minimum invariant that keeps Detect content
          // stable. Patient_summary remains the merge target; this is a
          // pure-additive forward-merge of one key.
          const incomingPS = sanitized.patientSummary || sanitized.patient_summary
          if (incomingPS && typeof incomingPS === 'object' && !incomingPS.detectPrep) {
            const prevPS = state.currentCase.patientSummary || state.currentCase.patient_summary
            if (prevPS && typeof prevPS === 'object' && prevPS.detectPrep) {
              const merged = { ...incomingPS, detectPrep: prevPS.detectPrep }
              if (sanitized.patientSummary) sanitized.patientSummary = merged
              if (sanitized.patient_summary) sanitized.patient_summary = merged
              console.log('[updateCase] Preserved detectPrep through patientSummary overwrite')
            }
          }

          const updatedCase = { 
            ...state.currentCase, 
            ...sanitized,
            status: newStatus,
            // Also preserve isShared if true
            isShared: state.currentCase.isShared || sanitized.isShared,
            shareCode: state.currentCase.shareCode || sanitized.shareCode,
          }
          
          return {
            currentCase: updatedCase,
            // Also update in cases array so CasesListScreen shows correct data
            cases: state.cases.map(c => c.id === updatedCase.id ? updatedCase : c),
          }
        })
      },
      
      // Update current case AND save to cases list in one operation (sanitized)
      updateAndSaveCase: async (updates) => {
        console.log('=== updateAndSaveCase called ===')
        console.log('Updates keys:', Object.keys(updates))
        if (updates.answers) {
          console.log('Answers keys:', Object.keys(updates.answers))
          console.log('Answers with values:', Object.entries(updates.answers).filter(([k, v]) => v && (Array.isArray(v) ? v.length > 0 : true)).length)
        }
        if (updates.voiceTextInput) {
          console.log('voiceTextInput length:', updates.voiceTextInput.length)
        }
        
        const sanitized = get().sanitizeData(updates)
        console.log('After sanitize - keys:', sanitized ? Object.keys(sanitized) : 'null')
        if (sanitized?.answers) {
          console.log('Sanitized answers keys:', Object.keys(sanitized.answers))
        }
        
        // v199.7: Collect fields explicitly set to null in the original updates.
        // sanitizeData strips nulls, but for withdrawal/cancel flows we MUST
        // null out fields like appointmentRequest, doctor, appointment, etc.
        // Without this, the old values survive in the store and reappear on CaseHome.
        const explicitNulls = {}
        for (const [key, value] of Object.entries(updates)) {
          if (value === null) {
            explicitNulls[key] = null
          }
        }
        
        let updatedCase = null
        
        set(state => {
          if (!state.currentCase) return state
          
          // Only preserve 'completed' status (immutable)
          // Allow 'claimed' -> 'scheduled' transition
          const currentStatus = state.currentCase.status
          const newStatus = currentStatus === 'completed' 
            ? 'completed' 
            : (sanitized.status || updates.status || currentStatus)
          
          updatedCase = { 
            ...state.currentCase, 
            ...sanitized,
            // v199.7: Apply explicit nulls AFTER sanitized spread to clear fields
            ...explicitNulls,
            status: newStatus,
            // Preserve isShared and shareCode UNLESS explicitly overridden
            // Check original `updates` (not sanitized) because sanitizeData strips null values
            isShared: 'isShared' in updates ? updates.isShared : state.currentCase.isShared,
            shareCode: 'shareCode' in updates ? updates.shareCode : state.currentCase.shareCode,
          }
          console.log('Updated case answers keys:', updatedCase.answers ? Object.keys(updatedCase.answers) : 'null')
          console.log('Updated case voiceTextInput present:', !!updatedCase.voiceTextInput)
          console.log('Updated case status:', updatedCase.status, 'isShared:', updatedCase.isShared)
          return {
            currentCase: updatedCase,
            cases: [...state.cases.filter(c => c.id !== updatedCase.id), updatedCase],
          }
        })
        
        // Sync to API after local update (important for describe data)
        // v164.3.0: Added adaptiveAnswers to sync triggers — previously adaptive answers
        // were saved locally but never sent to the API, so MD panel always showed "No response"
        if (updatedCase && (updates.describeComplete || updates.answers || updates.voiceTextInput || updates.voiceRecording || updates.adaptiveAnswers)) {
          try {
            const { casesAPI } = await import('../services/api')
            console.log('[SYNC] Syncing describe data to API for case:', updatedCase.id)
            console.log('[SYNC] Voice recording present:', !!updatedCase.voiceRecording?.audioData)
            if (updatedCase.voiceRecording?.audioData) {
              console.log('[SYNC] Voice recording size:', updatedCase.voiceRecording.audioData.length, 'chars')
            }
            
            // NOTE: Do NOT send status when syncing describe data
            // Status should only be changed by explicit actions (share, complete, etc.)
            // This prevents patient from accidentally overwriting doctor-set statuses like 'claimed'
            const syncPayload = {
              questionnaireResponses: withQuestionText(updatedCase.answers, updatedCase.questionText),
              voiceTextInput: updatedCase.voiceTextInput || '',
              describeSummary: updatedCase.describe?.summary || '',
              // v162.9.0: Only send describeComplete:true when THIS update explicitly sets it.
              // Previously read from updatedCase state, which could be stale-true from a prior
              // session, causing the API to auto-promote draft → ready_to_share accidentally.
              describeComplete: updates.describeComplete === true ? true : false,
            }
            
            // v164.3.0: Include adaptive answers when present
            if (updatedCase.adaptiveAnswers) {
              syncPayload.adaptiveAnswers = updatedCase.adaptiveAnswers
              console.log('[SYNC] Including adaptive answers in payload')
            }
            
            // Include voice recording if present
            // v242.58.0 (B64-2): prefer the storage PATH. `audioData` is the
            // legacy base64 data URL — writing it here is what put ~215 KB of
            // PHI audio per recording into the voice_recording_url column. New
            // recordings never carry audioData at all; the guard below keeps
            // reads of legacy in-memory cases working WITHOUT re-persisting
            // base64 on the next save.
            const _voiceRef = updatedCase.voiceRecording?.storagePath || null
            if (_voiceRef || updatedCase.voiceRecording?.transcript || updatedCase.voiceTranscript) {
              if (_voiceRef) syncPayload.voiceRecordingUrl = _voiceRef
              syncPayload.voiceDuration = updatedCase.voiceRecording?.duration || '0:00'
              syncPayload.voiceMimeType = updatedCase.voiceRecording?.mimeType || 'audio/webm'
              // Only include transcript if we actually have one - don't overwrite with pending message
              // The transcription happens async, so we check multiple sources for the actual transcript
              const actualTranscript = updatedCase.voiceTranscript || updatedCase.voiceRecording?.transcript || ''
              if (actualTranscript && !actualTranscript.includes('pending')) {
                syncPayload.voiceTranscript = actualTranscript
                console.log('[SYNC] Including actual voice transcript')
              } else if (updatedCase.voiceRecording?.transcriptionStatus === 'completed' && updatedCase.voiceRecording?.transcript) {
                syncPayload.voiceTranscript = updatedCase.voiceRecording.transcript
                console.log('[SYNC] Including completed transcription')
              }
              // Note: If no transcript yet, we don't send a "pending" message - let the actual transcript come through later
              console.log('[SYNC] Including voice recording in payload')
            }
            
            console.log('[SYNC] Sync payload keys:', Object.keys(syncPayload))
            await casesAPI.update(updatedCase.id, syncPayload)
            console.log('[SYNC] Describe data synced successfully')
          } catch (err) {
            console.error('[SYNC] Failed to sync describe data:', err)
          }
        }
        
        // Sync detect data (photos/videos/documents) to API
        console.log('[SYNC] Detect sync check:', { hasUpdatedCase: !!updatedCase, hasDetect: !!updates.detect, hasDetectComplete: !!updates.detectComplete, detectPhotos: updatedCase?.detect?.photos?.length || 0, detectVideos: updatedCase?.detect?.videos?.length || 0, detectDocs: updatedCase?.detect?.documents?.length || 0 })
        if (updatedCase && (updates.detect || updates.detectComplete)) {
          try {
            const { casesAPI, filesAPI } = await import('../services/api')
            console.log('[SYNC] Syncing detect data to API for case:', updatedCase.id)
            
            const detectPhotos = updatedCase.detect?.photos || []
            const detectVideos = updatedCase.detect?.videos || []
            const detectDocs = updatedCase.detect?.documents || []
            
            console.log('[SYNC] Detect files count - photos:', detectPhotos.length, 'videos:', detectVideos.length, 'documents:', detectDocs.length)
            
            // Helper: create metadata entry with storage path (no base64)
            // ★★ P2 Release 3 (PROD-167) — A STORAGE REFERENCE OR NOTHING. `file.url` is
            //   DetectScreen's local PREVIEW (the compressed data: URL of a photo, or a
            //   blob: URL), and a file whose upload below was refused keeps it. The old
            //   `file.storagePath || file.url` therefore wrote base64 PHI into cases.photos
            //   the moment /files/upload said 403. scripts/check-p2-access.mjs UP9 runs this.
            const isPersistableRef = (v) => typeof v === 'string' && v.length > 0 && !/^(data|blob):/i.test(v)
            const toMetadata = (file) => ({
              id: file.id,
              name: file.name,
              type: file.type,
              size: file.size,
              uploadedAt: file.uploadedAt,
              // Use storage path or an existing stored reference — never base64 or a blob: preview
              url: file.storagePath || (isPersistableRef(file.url) ? file.url : null),
              storagePath: file.storagePath || null,
              hasData: !!file.data,
              // s107: the pinned prior-test row this file was attached through (utils/priorTests.js)
              ...(file.priorRecord ? { priorRecord: file.priorRecord } : {}),
              // PROD-1198: the AI suggestion card this file was picked from (pages/detect/detectUploads.js)
              ...(file.suggestion ? { suggestion: file.suggestion } : {}),
            })
            
            // Upload files with base64 data to Supabase Storage
            const uploadToStorage = async (file, fileType) => {
              if (!file.data || file.storagePath) return file // Already uploaded or no data
              
              try {
                const ext = file.name?.split('.').pop() || (file.type?.includes('png') ? 'png' : 'jpg')
                const storagePath = `cases/${updatedCase.id}/${fileType}/${Date.now()}_${file.id}.${ext}`
                
                // Convert base64 data URL to blob for upload
                const base64Data = file.data.includes(',') ? file.data.split(',')[1] : file.data
                const binaryString = atob(base64Data)
                const bytes = new Uint8Array(binaryString.length)
                for (let i = 0; i < binaryString.length; i++) {
                  bytes[i] = binaryString.charCodeAt(i)
                }
                const blob = new Blob([bytes], { type: file.type || 'application/octet-stream' })
                
                const result = await filesAPI.upload(blob, storagePath)
                console.log(`[SYNC] Uploaded ${fileType} to storage:`, !!result?.path)
                
                return { ...file, storagePath: result.path, url: result.path, data: null }
              } catch (uploadErr) {
                // ★ P2 Release 3: status and code only (a refused key is not logged here).
                console.warn(`[SYNC] ${fileType} upload refused or failed:`, uploadErr?.status || 0, uploadErr?.code || '')
                return file // Keep original on failure; toMetadata keeps only a stored reference
              }
            }
            
            // Upload photos, videos, and documents to storage
            const uploadedPhotos = await Promise.all(
              detectPhotos.map(p => uploadToStorage(p, 'photos'))
            )
            const uploadedVideos = await Promise.all(
              detectVideos.map(v => uploadToStorage(v, 'videos'))
            )
            const uploadedDocs = await Promise.all(
              detectDocs.map(d => uploadToStorage(d, 'documents'))
            )
            
            // Build payload with storage paths only (no base64)
            const syncPayload = {
              photos: uploadedPhotos.map(toMetadata),
              videos: uploadedVideos.map(toMetadata),
              documents: uploadedDocs.map(toMetadata),
              // ★ s117 (TestFlight b84, Dr. Rami: "I had clicked nothing to share … does not seem to have persisted"):
              //   "Nothing to add" was only on the phone, so a reload derived Detect from the (empty) file lists and
              //   showed it Open again. The server keeps it in detect_data.skipReason (merged, and already in the slim
              //   case list), and loadCases reads it back.
              ...(updatedCase.detect?.noFilesToAdd ? { detectData: { skipReason: 'nothing_to_add' } } : {}),
            }
            
            console.log('[SYNC] Sending detect data to API (storage paths, no base64):', {
              photos: syncPayload.photos.length,
              videos: syncPayload.videos.length,
              documents: syncPayload.documents.length,
            })
            
            await casesAPI.update(updatedCase.id, syncPayload)
            console.log('[SYNC] Detect data synced successfully')
            
          } catch (err) {
            console.error('[SYNC] Failed to sync detect data:', err)
          }
        }

        // Sync deliver data (voice memo, symptom survey, medication review, task completions) to API
        if (updatedCase && updates.deliver) {
          try {
            const { casesAPI } = await import('../services/api')
            console.log('[SYNC] Syncing deliver data to API for case:', updatedCase.id)
            console.log('[SYNC] Deliver keys:', Object.keys(updatedCase.deliver || {}))
            await casesAPI.update(updatedCase.id, { deliver: updatedCase.deliver })
            console.log('[SYNC] Deliver data synced successfully')
          } catch (err) {
            console.error('[SYNC] Failed to sync deliver data:', err)
          }
        }

        // v227.7.4: Durable-field persistence (Option B). updateAndSaveCase is
        // local-first and only auto-syncs the describe / detect / deliver payloads
        // above. Any OTHER durable case field used to be local-only — the root cause
        // of the doctor_source-on-share bug. Persist a whitelist of PUT-mapped fields
        // here so they survive a reload. status / shareCode / isShared are
        // intentionally EXCLUDED: status is doctor-protected, and share fields go
        // through the dedicated /share endpoint. Built from the original `updates`
        // (not sanitized) so explicit nulls — e.g. withdrawal clears — persist too.
        if (updatedCase) {
          const DURABLE_CASE_FIELDS = [
            'doctorSource', 'doctor_source',
            'preVisitSummary', 'pre_visit_summary',
            'follow_up',
            'encounterPhase', 'encounter_phase',
            'currentEncounter', 'current_encounter',
            'diagnoseData',
            'externalDoctorNote', 'external_doctor_note',
            'externalDoctor', 'external_doctor',
            'appointmentRequest', 'appointment_request',
          ]
          const persistPayload = {}
          for (const key of DURABLE_CASE_FIELDS) {
            if (key in updates) persistPayload[key] = updates[key]
          }
          if (Object.keys(persistPayload).length > 0) {
            try {
              const { casesAPI } = await import('../services/api')
              console.log('[PERSIST] Persisting durable fields to API:', Object.keys(persistPayload))
              await casesAPI.update(updatedCase.id, persistPayload)
              console.log('[PERSIST] Durable fields persisted')
            } catch (err) {
              console.warn('[PERSIST] Durable field persist failed (non-blocking):', err)
            }
          }
        }
      },
      
      // Clear all cases (used on logout)
      clearAll: () => set({ cases: [], currentCase: null }),
      // v242.39.0: full reset for account switch / logout. Wipes in-memory PHI,
      // re-arms casesLoading so the next account sees the loader (not stale cases),
      // clears deletedCaseIds, and drops the persisted copy so a later reload can't
      // rehydrate the previous account's cases.
      resetForUser: () => {
        set({ cases: [], currentCase: null, casesLoading: true, casesLoadFailed: false, deletedCaseIds: [] })
        try { localStorage.removeItem('orella-cases') } catch (_) {}
      },
      
      // Generate share code for current case
      generateShareCode: async (requirePin = true) => {
        const currentCase = get().currentCase
        if (!currentCase) return null
        
        set({ syncing: true })
        
        try {
          const { casesAPI } = await import('../services/api')
          const response = await casesAPI.generateShareCode(currentCase.id, requirePin)
          
          // Update local case with share code - BOTH currentCase AND cases array
          set(state => {
            const updatedCase = {
              ...state.currentCase,
              shareCode: response.shareCode,
              sharePin: response.pin,
              shareExpiration: response.expiresAt,
              status: 'shared',
              isShared: true,
            }
            
            return {
              currentCase: updatedCase,
              // Also update the case in the cases array
              cases: state.cases.map(c => 
                c.id === currentCase.id ? updatedCase : c
              ),
              syncing: false,
            }
          })
          
          return {
            shareCode: response.shareCode,
            pin: response.pin,
            expiresAt: response.expiresAt,
          }
        } catch (err) {
          console.error('Error generating share code:', err)
          set({ syncing: false })
          return null
        }
      },
    }),
    { 
      name: 'orella-cases',
      // v242.160.0 — `casesLoading` was already re-armed in onRehydrateStorage
      // below, which is why it never deadlocked; `syncing` had no such cover.
      // Not writing either is the general remedy rather than the two patches.
      partialize: (state) =>
        Object.fromEntries(CASE_STORE_PERSISTED_KEYS.map((k) => [k, state[k]])),
      merge: (persisted, current) => {
        const clean = { ...(persisted || {}) }
        for (const k of CASE_STORE_TRANSIENT_KEYS) delete clean[k]
        return { ...current, ...clean }
      },
      // Handle storage errors gracefully
      storage: {
        getItem: (name) => {
          // v220.11 ONE-SHOT PURGE: Any persisted state from before v220.11
          // may contain prefilled answers that were never tagged in
          // prefillKeys. On those builds the merge rule "existing answers
          // win" allowed stale prefills to override fresh AI extraction
          // forever (root cause of "fixed it twice still broken"). We
          // detect the absence of the v220.11 marker and nuke the cases
          // cache once. Auth/profile stores have their own marker and are
          // untouched. After this one-time clear the marker is written
          // (below in setItem via minimalValue.state.__prefillKeysV) and
          // subsequent loads skip the purge.
          try {
            const marker = localStorage.getItem('orella-cases-prefill-v')
            if (marker !== '220.11') {
              localStorage.removeItem(name)
              localStorage.setItem('orella-cases-prefill-v', '220.11')
              console.log('[Store] v220.11 one-shot purge: cleared stale cases cache')
              return null
            }
          } catch (_) { /* non-fatal */ }
          const str = localStorage.getItem(name)
          if (!str) return null
          try {
            return JSON.parse(str)
          } catch (e) {
            console.warn('Error parsing stored state:', e)
            return null
          }
        },
        setItem: (name, value) => {
          try {
            // Try to save minimal data only - just IDs and basic state
            const minimalValue = {
              ...value,
              state: {
                ...value.state,
                // Only keep essential case info, not full case data
                cases: (value.state?.cases || []).map(c => ({
                  id: c.id,
                  status: c.status,
                  caseNumber: c.caseNumber,
                  specialtyId: c.specialtyId,
                  specialtyName: c.specialtyName,
                  focusAreaId: c.focusAreaId,
                  focusAreaName: c.focusAreaName,
                  createdAt: c.createdAt,
                  completedAt: c.completedAt,
                  // v138.0.0: Persist phase-critical flags to prevent rehydration backslip
                  describeComplete: c.describeComplete || false,
                  detectComplete: c.detectComplete || false,
                  isShared: c.isShared || false,
                  // ★ U2 push 2 review r1 m3 (PROD-416): who holds the case survives a reload, so a
                  //   held draft offers no Delete from the cached list before loadCases answers.
                  heldByDoctor: c.heldByDoctor === true,
                  shareCode: c.shareCode || null,
                  doctorSource: c.doctorSource || null,
                  doctor_source: c.doctor_source || null,
                  encounterPhase: c.encounterPhase || '',
                  currentEncounter: c.currentEncounter || 1,
                  diagnoseAcknowledged: c.diagnoseAcknowledged || false,
                  diagnoseAcknowledgedAt: c.diagnoseAcknowledgedAt || null,
                  // Keep followUp requirements for task display
                  followUp: c.followUp ? {
                    type: c.followUp.type,
                    timeframe: c.followUp.timeframe,
                    visitType: c.followUp.visitType,
                    requirements: c.followUp.requirements,
                    ordersPlaced: c.followUp.ordersPlaced
                  } : null
                })),
                currentCase: value.state?.currentCase ? {
                  id: value.state.currentCase.id,
                  status: value.state.currentCase.status,
                  caseNumber: value.state.currentCase.caseNumber,
                  specialtyId: value.state.currentCase.specialtyId,
                  specialtyName: value.state.currentCase.specialtyName,
                  focusAreaId: value.state.currentCase.focusAreaId,
                  focusAreaName: value.state.currentCase.focusAreaName,
                  followUp: value.state.currentCase.followUp,
                  // v151.3.0: Persist answers so draft survey repopulates on resume
                  answers: value.state.currentCase.answers || {},
                  // v220.11: Persist prefillKeys alongside answers so the
                  // tag→strip→re-prefill cycle works across browser sessions.
                  // Without this, stale prefilled answers would survive a
                  // page refresh untagged and look user-entered, defeating
                  // the architectural fix.
                  prefillKeys: value.state.currentCase.prefillKeys || {},
                  voiceTextInput: value.state.currentCase.voiceTextInput || '',
                  // v164.3.0: Persist adaptive Q&A so data survives tab close/refresh
                  adaptiveQuestions: value.state.currentCase.adaptiveQuestions || null,
                  adaptiveAnswers: value.state.currentCase.adaptiveAnswers || null,
                  // v138.0.0: Persist phase-critical flags to prevent rehydration backslip
                  describeComplete: value.state.currentCase.describeComplete || false,
                  detectComplete: value.state.currentCase.detectComplete || false,
                  isShared: value.state.currentCase.isShared || false,
                  heldByDoctor: value.state.currentCase.heldByDoctor === true, // m3 (PROD-416)
                  shareCode: value.state.currentCase.shareCode || null,
                  doctorSource: value.state.currentCase.doctorSource || null,
                  doctor_source: value.state.currentCase.doctor_source || null,
                  encounterPhase: value.state.currentCase.encounterPhase || '',
                  currentEncounter: value.state.currentCase.currentEncounter || 1,
                  // v242.92.0 — the Deliver card's state and the Detect card's
                  //   examination both read from these; without them the stage
                  //   carousel forgets what it knew on every reload.
                  deliverState: value.state.currentCase.deliverState || null,
                  patientSummary: value.state.currentCase.patientSummary || null,
                  preVisitSummary: value.state.currentCase.preVisitSummary || null,
                  diagnoseAcknowledged: value.state.currentCase.diagnoseAcknowledged || false,
                  diagnoseAcknowledgedAt: value.state.currentCase.diagnoseAcknowledgedAt || null,
                  diagnose: value.state.currentCase.diagnose ? {
                    selectedTreatments: value.state.currentCase.diagnose.selectedTreatments,
                    selectedAdditionalOrders: value.state.currentCase.diagnose.selectedAdditionalOrders,
                    selectedWorkup: value.state.currentCase.diagnose.selectedWorkup
                  } : null
                } : null
              }
            }
            // v151.6.1: Persist deletedCaseIds so deleted drafts don't reappear on reload
            if (value.state?.deletedCaseIds?.length > 0) {
              minimalValue.state.deletedCaseIds = value.state.deletedCaseIds
            }
            localStorage.setItem(name, JSON.stringify(minimalValue))
          } catch (e) {
            console.error('Error saving to localStorage:', e)
            // Just clear it - API will reload everything
            try {
              localStorage.removeItem(name)
            } catch (e2) {
              console.error('Failed to clear storage:', e2)
            }
          }
        },
        removeItem: (name) => localStorage.removeItem(name),
      },
      onRehydrateStorage: () => (state, error) => {
        if (error) {
          console.error('Error rehydrating store:', error)
        } else {
          console.log('Store rehydrated:', state ? 'with data' : 'empty')
          // v143.4: Always reset casesLoading on rehydrate — fresh API fetch needed
          if (state) state.casesLoading = true
        }
      },
    }
  )
)

// Export questionnaire store
export { default as useQuestionnaireStore } from './useQuestionnaireStore'

// Export notification store
export { useNotificationStore } from './useNotificationStore'

// v213.5 (C-A-OB-1): Export onboarding store
export { useOnboardingStore } from './useOnboardingStore'
